import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, FileText, Plus, Trash2, Edit, Edit2, Eye, Search, Palette, Check, X, Save, RefreshCw, Copy, ChevronRight, ChevronDown, Folder, FolderOpen, File, Globe, ExternalLink, GripVertical, Type, Image, List, Grid3X3, MessageSquare, Maximize2, Layers, Sparkles, Settings2, ImagePlus, ChevronUp, FolderPlus, Home, FileEdit, MoveRight } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import RichTextEditor from '../components/RichTextEditor';
import InlineStructuredContentEditor from '../components/InlineStructuredContentEditor';
import CMSImagePicker from '../components/CMSImagePicker';

// Color field definitions for theme editor
const colorFields = [
  { key: 'primary', label: 'Primary Color', description: 'Main brand color used across the site' },
  { key: 'primary_hover', label: 'Primary Hover', description: 'Hover state for primary elements' },
  { key: 'primary_light', label: 'Primary Light', description: 'Light variant for backgrounds' },
  { key: 'secondary', label: 'Secondary Color', description: 'Secondary brand color' },
  { key: 'accent', label: 'Accent Color', description: 'Accent highlights and icons' },
  { key: 'ui_blue', label: 'UI Blue', description: 'Blue color for tabs, progress bars, text highlights' },
  { key: 'ui_blue_light', label: 'UI Blue Light', description: 'Light blue for backgrounds and borders' },
  { key: 'header_bg', label: 'Header Background', description: 'Page header backgrounds' },
  { key: 'button_bg', label: 'Button Background', description: 'Primary button background' },
  { key: 'button_hover', label: 'Button Hover', description: 'Button hover state' },
  { key: 'link_color', label: 'Link Color', description: 'Hyperlink text color' },
  { key: 'border_focus', label: 'Focus Border', description: 'Input focus border color' },
  { key: 'gradient_start', label: 'Gradient Start', description: 'Gradient starting color' },
  { key: 'gradient_end', label: 'Gradient End', description: 'Gradient ending color' },
];

// Default color values
const defaultColors = {
  primary: 'rgb(41, 173, 211)',
  primary_hover: 'rgb(31, 153, 191)',
  primary_light: 'rgba(41, 173, 211, 0.1)',
  secondary: 'rgb(31, 163, 201)',
  accent: 'rgb(41, 173, 211)',
  ui_blue: 'rgb(59, 130, 246)',
  ui_blue_light: 'rgb(219, 234, 254)',
  header_bg: 'rgb(41, 173, 211)',
  button_bg: 'rgb(41, 173, 211)',
  button_hover: 'rgb(31, 153, 191)',
  link_color: 'rgb(41, 173, 211)',
  border_focus: 'var(--theme-primary)',
  gradient_start: 'var(--theme-primary)',
  gradient_end: 'var(--theme-secondary)'
};

// Convert RGB string to hex for color picker
const rgbToHex = (rgb) => {
  if (!rgb) return 'var(--theme-primary)';
  if (rgb.startsWith('#')) return rgb;
  
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return 'var(--theme-primary)';
  
  const r = parseInt(match[0]);
  const g = parseInt(match[1]);
  const b = parseInt(match[2]);
  
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
};

// Convert hex to RGB string
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
  }
  return hex;
};

// Parse RGB string to components
const parseRgb = (color) => {
  if (!color) return { r: 41, g: 173, b: 211 };
  const match = color.match(/\d+/g);
  if (!match || match.length < 3) return { r: 41, g: 173, b: 211 };
  return {
    r: parseInt(match[0]),
    g: parseInt(match[1]),
    b: parseInt(match[2])
  };
};

// Darken a color by percentage
const darkenColor = (color, percent = 20) => {
  const { r, g, b } = parseRgb(color);
  const factor = 1 - (percent / 100);
  return `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`;
};

// Lighten a color (create light variant with transparency)
const lightenColor = (color, opacity = 0.1) => {
  const { r, g, b } = parseRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Generate all derived colors from primary
const generateColorsFromPrimary = (primaryColor) => {
  const hover = darkenColor(primaryColor, 20);
  const light = lightenColor(primaryColor, 0.1);
  
  return {
    primary: primaryColor,
    primary_hover: hover,
    primary_light: light,
    secondary: darkenColor(primaryColor, 10),
    accent: primaryColor,
    ui_blue: primaryColor,
    ui_blue_light: light,
    header_bg: primaryColor,
    button_bg: primaryColor,
    button_hover: hover,
    link_color: primaryColor,
    border_focus: primaryColor,
    gradient_start: primaryColor,
    gradient_end: hover
  };
};

// Original default colors for reset
const originalDefaultColors = {
  primary: 'rgb(41, 173, 211)',
  primary_hover: 'rgb(31, 153, 191)',
  primary_light: 'rgba(41, 173, 211, 0.1)',
  secondary: 'rgb(31, 163, 201)',
  accent: 'rgb(41, 173, 211)',
  ui_blue: 'rgb(59, 130, 246)',
  ui_blue_light: 'rgb(219, 234, 254)',
  header_bg: 'rgb(41, 173, 211)',
  button_bg: 'rgb(41, 173, 211)',
  button_hover: 'rgb(31, 153, 191)',
  link_color: 'rgb(41, 173, 211)',
  border_focus: 'rgb(41, 173, 211)',
  gradient_start: 'rgb(41, 173, 211)',
  gradient_end: 'rgb(31, 163, 201)'
};

// Check if a color is light (for text contrast)
const isLightColor = (color) => {
  if (!color) return false;
  const match = color.match(/\d+/g);
  if (!match || match.length < 3) return false;
  const r = parseInt(match[0]);
  const g = parseInt(match[1]);
  const b = parseInt(match[2]);
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// Tree Node Component for page navigation
const TreeNode = ({ nodeKey, node, level, expandedNodes, toggleNode, selectedPage, selectPage, pages, folders, openCreatePageModal, onContextMenu }) => {
  const hasChildren = node.children && Object.keys(node.children).length > 0;
  const isExpanded = expandedNodes.has(nodeKey);
  const isSelected = selectedPage === node.path;
  const hasContent = pages.some(p => p.path === node.path);
  const isFolder = folders?.some(f => f.path === node.path) || (hasChildren && !hasContent);
  
  const paddingLeft = level * 16 + 8;

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e, node);
    }
  };
  
  return (
    <div>
      <div
        className={`flex items-center gap-1 py-1.5 px-2 rounded-lg cursor-pointer transition-colors ${
          isSelected ? 'bg-theme-blue text-white' : 'text-gray-700 hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${paddingLeft}px` }}
        onClick={() => {
          if (hasChildren || isFolder) {
            toggleNode(nodeKey);
          }
          // Only select page if it has content (not a pure folder)
          if (node.path && !isFolder) {
            selectPage(node.path);
          } else if (node.path && isFolder) {
            // For folders, just toggle expansion
            toggleNode(nodeKey);
          }
        }}
        onContextMenu={handleContextMenu}
      >
        {(hasChildren || isFolder) ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleNode(nodeKey);
            }}
            className={`p-0.5 rounded ${isSelected ? 'hover:bg-blue-600' : 'hover:bg-gray-200'}`}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        
        {(hasChildren || isFolder) ? (
          isExpanded ? (
            <FolderOpen className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-yellow-500'}`} />
          ) : (
            <Folder className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-yellow-500'}`} />
          )
        ) : (
          <File className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
        )}
        
        <span className="text-sm truncate flex-1">{node.label}</span>
        
        {/* Only show green dot for actual page content, not folders */}
        {hasContent && !isFolder && (
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-green-300' : 'bg-green-500'}`} title="Has content" />
        )}
      </div>
      
      {(hasChildren || isFolder) && isExpanded && (
        <div>
          {Object.entries(node.children || {}).map(([childKey, childNode]) => (
            <TreeNode
              key={childKey}
              nodeKey={childKey}
              node={childNode}
              level={level + 1}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              selectedPage={selectedPage}
              selectPage={selectPage}
              pages={pages}
              folders={folders}
              openCreatePageModal={openCreatePageModal}
              onContextMenu={onContextMenu}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContentManager = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('themes');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { refreshTheme } = useTheme();
  
  // Themes state
  const [themes, setThemes] = useState([]);
  const [editingTheme, setEditingTheme] = useState(null);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingTheme, setApplyingTheme] = useState(false);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [editingInnerTemplate, setEditingInnerTemplate] = useState(null);
  const [showInnerTemplateModal, setShowInnerTemplateModal] = useState(false);
  
  // Available public pages for inner page templates
  const availablePublicPages = [
    // About Menu
    { path: '/our-mission', label: 'Our Mission', category: 'About' },
    { path: '/our-core-values', label: 'Our Core Values', category: 'About' },
    { path: '/our-team', label: 'Our Team', category: 'About' },
    { path: '/gallery', label: 'Gallery', category: 'About' },
    { path: '/review-us', label: 'Review Us', category: 'About' },
    // Services - General
    { path: '/specialty-services', label: 'Specialty Services', category: 'Services' },
    { path: '/urgent-care', label: 'Urgent Care', category: 'Services' },
    { path: '/dental-cleanings', label: 'Dental Cleanings', category: 'Services' },
    { path: '/pet-vaccinations', label: 'Pet Vaccinations', category: 'Services' },
    { path: '/pet-microchipping', label: 'Pet Microchipping', category: 'Services' },
    { path: '/preventive-pet-care', label: 'Preventive Pet Care', category: 'Services' },
    { path: '/ultrasound-exams', label: 'Ultrasound Exams', category: 'Services' },
    { path: '/veterinary-diagnostic-services', label: 'Diagnostic Services', category: 'Services' },
    { path: '/pet-dermatology-allergy-care', label: 'Dermatology & Allergy', category: 'Services' },
    { path: '/digital-veterinary-x-rays', label: 'Digital X-Rays', category: 'Services' },
    { path: '/end-of-life-care', label: 'End of Life Care', category: 'Services' },
    { path: '/on-site-pharmacy', label: 'On Site Pharmacy', category: 'Services' },
    { path: '/pet-travel-certificates', label: 'Travel Certificates', category: 'Services' },
    { path: '/pet-tooth-extraction', label: 'Tooth Extraction', category: 'Services' },
    { path: '/pet-spay-neuter', label: 'Spay & Neuter', category: 'Services' },
    { path: '/pet-bladder-stone-removal', label: 'Bladder Stone Removal', category: 'Services' },
    { path: '/blocked-cats-urgent-care', label: 'Blocked Cats Care', category: 'Services' },
    { path: '/foreign-body-surgery', label: 'Foreign Body Surgery', category: 'Services' },
    { path: '/pet-ocular-services', label: 'Eye Care Services', category: 'Services' },
    // Dog Services
    { path: '/dog-dental-care', label: 'Dog Dental Care', category: 'Dog Services' },
    { path: '/dog-skin-care', label: 'Dog Skin Care', category: 'Dog Services' },
    { path: '/dog-vaccinations', label: 'Dog Vaccinations', category: 'Dog Services' },
    { path: '/dog-wellness-exams', label: 'Dog Wellness Exams', category: 'Dog Services' },
    { path: '/dog-eye-care', label: 'Dog Eye Care', category: 'Dog Services' },
    { path: '/dog-surgeries', label: 'Dog Surgeries', category: 'Dog Services' },
    { path: '/dog-diet-nutrition', label: 'Dog Diet & Nutrition', category: 'Dog Services' },
    // Cat Services
    { path: '/cat-dental-care', label: 'Cat Dental Care', category: 'Cat Services' },
    { path: '/cat-diagnostic-imaging', label: 'Cat Diagnostic Imaging', category: 'Cat Services' },
    { path: '/cat-vaccinations', label: 'Cat Vaccinations', category: 'Cat Services' },
    // Patient Menu
    { path: '/your-first-visit', label: 'Your First Visit', category: 'Patient' },
    { path: '/pet-insurance-payments', label: 'Insurance & Payments', category: 'Patient' },
  ];
  
  // Pages state
  const [pages, setPages] = useState([]);
  
  // CMS state
  const [pageStructure, setPageStructure] = useState({});
  const [selectedPage, setSelectedPage] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set(['about', 'services', 'patient']));
  const [showCreatePageModal, setShowCreatePageModal] = useState(false);
  const [newPageParent, setNewPageParent] = useState(null);
  const [newPageLayout, setNewPageLayout] = useState('default');

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, node: null });
  const contextMenuRef = useRef(null);
  const treeContainerRef = useRef(null);

  // Folder creation modal state
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderParentPath, setFolderParentPath] = useState('/');

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'page' | 'folder', path: string, title: string }
  const [deleting, setDeleting] = useState(false);

  // Move page modal state
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // { id: string, path: string, title: string }
  const [selectedMoveFolder, setSelectedMoveFolder] = useState('/');
  const [moving, setMoving] = useState(false);

  // Custom folders state
  const [folders, setFolders] = useState([]);

  // Components state
  const [components, setComponents] = useState([]);
  const [editingComponent, setEditingComponent] = useState(null);
  const [showComponentModal, setShowComponentModal] = useState(false);
  
  // Page Component Config Editor state
  const [editingPageComponent, setEditingPageComponent] = useState(null);
  const [editingPageComponentIndex, setEditingPageComponentIndex] = useState(null);
  const [showPageComponentModal, setShowPageComponentModal] = useState(false);

  // Slider images state for home hero component
  const [availableSliderImages, setAvailableSliderImages] = useState([]);
  const [sliderImagesLoading, setSliderImagesLoading] = useState(false);

  // Product Listing component state
  const [productListingCategories, setProductListingCategories] = useState({
    products: [],  // Menu categories for products
    services: [],  // Menu categories for services
    boarding: []   // Boarding spaces
  });
  const [productSubCategories, setProductSubCategories] = useState([]); // Product categories within selected menu
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);

  // AI Content Generator state
  const [generatorUrl, setGeneratorUrl] = useState('');
  const [generatingContent, setGeneratingContent] = useState(false);
  const [importImages, setImportImages] = useState(false);
  const [enableWebResearch, setEnableWebResearch] = useState(false);
  const [showRawContentModal, setShowRawContentModal] = useState(false);
  const [rawContent, setRawContent] = useState('');
  const [generatingFromRaw, setGeneratingFromRaw] = useState(false);
  const [rawContentWebResearch, setRawContentWebResearch] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    const origin = window.location.origin;
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8001';
    }
    return origin;
  };

  const backendUrl = getBackendURL();

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'themes') {
      fetchThemes();
    } else if (activeTab === 'templates') {
      fetchTemplates();
    } else if (activeTab === 'pages') {
      fetchPages();
      fetchComponents(); // Fetch components for page editor
    } else if (activeTab === 'components') {
      fetchComponents();
    }
  }, [activeTab]);

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/website-themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThemes(response.data.themes || []);
    } catch (error) {
      console.error('Error fetching themes:', error);
      setThemes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/page-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const activateTemplate = async (templateId) => {
    setApplyingTheme(true); // Reuse the applying modal
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${backendUrl}/api/page-templates/${templateId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTemplates();
      await new Promise(resolve => setTimeout(resolve, 500));
      showNotification('Template activated successfully');
    } catch (error) {
      console.error('Error activating template:', error);
      showNotification(error.response?.data?.detail || 'Error activating template', 'error');
    } finally {
      setApplyingTheme(false);
    }
  };

  // Inner Page Template operations
  const openNewInnerTemplateModal = () => {
    setEditingInnerTemplate({
      name: '',
      description: '',
      page_type: 'inner',
      layout: 'image_right',
      applied_pages: [],
      is_active: true
    });
    setShowInnerTemplateModal(true);
  };

  const openEditInnerTemplateModal = (template) => {
    setEditingInnerTemplate({ ...template });
    setShowInnerTemplateModal(true);
  };

  const closeInnerTemplateModal = () => {
    setShowInnerTemplateModal(false);
    setEditingInnerTemplate(null);
  };

  const togglePageSelection = (pagePath) => {
    if (!editingInnerTemplate) return;
    const currentPages = editingInnerTemplate.applied_pages || [];
    const newPages = currentPages.includes(pagePath)
      ? currentPages.filter(p => p !== pagePath)
      : [...currentPages, pagePath];
    setEditingInnerTemplate({ ...editingInnerTemplate, applied_pages: newPages });
  };

  const saveInnerTemplate = async () => {
    if (!editingInnerTemplate) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingInnerTemplate.id) {
        // Update existing
        await axios.put(`${backendUrl}/api/page-templates/${editingInnerTemplate.id}`, editingInnerTemplate, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Inner page template updated successfully');
      } else {
        // Create new
        await axios.post(`${backendUrl}/api/page-templates`, editingInnerTemplate, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Inner page template created successfully');
      }
      await fetchTemplates();
      closeInnerTemplateModal();
    } catch (error) {
      console.error('Error saving inner template:', error);
      showNotification(error.response?.data?.detail || 'Error saving template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteInnerTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendUrl}/api/page-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTemplates();
      showNotification('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      showNotification(error.response?.data?.detail || 'Error deleting template', 'error');
    }
  };

  // Components functions
  const fetchComponents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/cms/components`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComponents(response.data.components || []);
    } catch (error) {
      console.error('Error fetching components:', error);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSliderImages = async () => {
    setSliderImagesLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/slider-images`);
      if (response.data && response.data.slider_images) {
        setAvailableSliderImages(response.data.slider_images.sort((a, b) => (a.order || 0) - (b.order || 0)));
      }
    } catch (error) {
      console.error('Error fetching slider images:', error);
      setAvailableSliderImages([]);
    } finally {
      setSliderImagesLoading(false);
    }
  };

  // Fetch categories for product listing component
  const fetchProductListingCategories = async () => {
    setLoadingCategories(true);
    try {
      const token = localStorage.getItem('token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [productsRes, servicesRes, boardingRes] = await Promise.all([
        axios.get(`${backendUrl}/api/menu-categories`).catch((e) => {
          console.log('Error fetching menu categories:', e.message);
          return { data: [] };
        }),
        axios.get(`${backendUrl}/api/appointment-service-menu-categories/public`).catch((e) => {
          console.log('Error fetching service categories:', e.message);
          return { data: { menu_categories: [] } };
        }),
        axios.get(`${backendUrl}/api/boarding-spaces`, { headers: authHeaders }).catch((e) => {
          console.log('Error fetching boarding spaces:', e.message);
          return { data: [] };
        })
      ]);
      
      console.log('Products categories response:', productsRes.data);
      console.log('Services categories response:', servicesRes.data);
      console.log('Boarding spaces response:', boardingRes.data);
      
      setProductListingCategories({
        products: Array.isArray(productsRes.data) ? productsRes.data : [],
        services: servicesRes.data?.menu_categories || [],
        boarding: Array.isArray(boardingRes.data) ? boardingRes.data : []
      });
    } catch (error) {
      console.error('Error fetching product listing categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch product sub-categories within a selected menu category
  const fetchProductSubCategories = async (menuCategoryId) => {
    if (!menuCategoryId) {
      setProductSubCategories([]);
      return;
    }
    
    setLoadingSubCategories(true);
    try {
      const response = await axios.get(`${backendUrl}/api/menu-items`, {
        params: { menu_category_id: menuCategoryId }
      });
      
      // Extract unique product categories from items
      const items = Array.isArray(response.data) ? response.data : [];
      const uniqueCategories = [...new Set(items.map(item => item.category).filter(Boolean))].sort();
      
      console.log('Product sub-categories:', uniqueCategories);
      setProductSubCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching product sub-categories:', error);
      setProductSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const openNewComponentModal = () => {
    setEditingComponent({
      name: '',
      slug: '',
      type: 'cta_section',
      description: '',
      config: {
        title: '',
        subtitle: '',
        button_text: '',
        button_link: '',
        show_phone: true,
        background_style: 'gradient'
      },
      is_published: true
    });
    setShowComponentModal(true);
  };

  const openEditComponentModal = (component) => {
    setEditingComponent({ ...component });
    setShowComponentModal(true);
    // Fetch slider images if this is a home_hero component
    if (component.type === 'home_hero') {
      fetchSliderImages();
    }
    // Fetch categories if this is a product_listing component
    if (component.type === 'product_listing') {
      fetchProductListingCategories();
      // Also fetch sub-categories if a menu category is already selected
      if (component.config?.selected_menu_category) {
        fetchProductSubCategories(component.config.selected_menu_category);
      }
    }
  };

  const closeComponentModal = () => {
    setShowComponentModal(false);
    setEditingComponent(null);
  };

  const saveComponent = async () => {
    if (!editingComponent) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingComponent.id) {
        await axios.put(`${backendUrl}/api/cms/components/${editingComponent.id}`, editingComponent, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Component updated successfully');
      } else {
        await axios.post(`${backendUrl}/api/cms/components`, editingComponent, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Component created successfully');
      }
      await fetchComponents();
      closeComponentModal();
    } catch (error) {
      console.error('Error saving component:', error);
      showNotification(error.response?.data?.detail || 'Error saving component', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteComponent = async (componentId) => {
    if (!window.confirm('Are you sure you want to delete this component?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendUrl}/api/cms/components/${componentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchComponents();
      showNotification('Component deleted successfully');
    } catch (error) {
      console.error('Error deleting component:', error);
      showNotification(error.response?.data?.detail || 'Error deleting component', 'error');
    }
  };

  const fetchPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Fetch page structure
      const structureResponse = await axios.get(`${backendUrl}/api/cms/page-structure`);
      setPageStructure(structureResponse.data.structure || {});
      
      // Fetch all CMS pages
      const pagesResponse = await axios.get(`${backendUrl}/api/cms/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(pagesResponse.data.pages || []);
      
      // Fetch all CMS folders
      try {
        const foldersResponse = await axios.get(`${backendUrl}/api/cms/folders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Fetched folders:', foldersResponse.data);
        setFolders(foldersResponse.data.folders || []);
      } catch (err) {
        console.error('Error fetching folders:', err);
        setFolders([]);
      }
    } catch (error) {
      console.error('Error fetching pages:', error);
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  // CMS functions
  const toggleNode = (nodeId, elementRef = null) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      const isExpanding = !newSet.has(nodeId);
      
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
        // Auto-scroll to show expanded content after a brief delay
        setTimeout(() => {
          if (treeContainerRef.current) {
            const container = treeContainerRef.current;
            const containerRect = container.getBoundingClientRect();
            const containerBottom = containerRect.bottom;
            
            // Check if we need to scroll - if the click was in the bottom 40% of the container
            const clickY = elementRef?.getBoundingClientRect?.()?.top || containerBottom - 100;
            const threshold = containerRect.top + (containerRect.height * 0.6);
            
            if (clickY > threshold) {
              // Smooth scroll down to reveal expanded content
              container.scrollBy({ top: 150, behavior: 'smooth' });
            }
          }
        }, 50);
      }
      return newSet;
    });
  };

  const selectPage = async (pagePath) => {
    setSelectedPage(pagePath);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/cms/pages${pagePath}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.page) {
        // Ensure components array exists
        setEditingPage({
          ...response.data.page,
          components: response.data.page.components || []
        });
      } else {
        // Create a new page object for editing
        const pageInfo = findPageInStructure(pagePath);
        setEditingPage({
          path: pagePath,
          title: pageInfo?.label || 'New Page',
          badge_text: '',
          description: '',
          hero_image: '',
          content_sections: [],
          components: [],
          layout: 'default',
          is_published: false,
          is_custom: false
        });
      }
    } catch (error) {
      console.error('Error fetching page:', error);
    }
  };

  const findPageInStructure = (path, structure = pageStructure) => {
    for (const key in structure) {
      const node = structure[key];
      if (node.path === path) {
        return node;
      }
      if (node.children) {
        const found = findPageInStructure(path, node.children);
        if (found) return found;
      }
    }
    return null;
  };

  const savePage = async () => {
    if (!editingPage) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingPage.id) {
        // Update existing page
        await axios.put(`${backendUrl}/api/cms/pages/${editingPage.id}`, editingPage, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Page updated successfully');
      } else {
        // Create new page
        const response = await axios.post(`${backendUrl}/api/cms/pages`, editingPage, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEditingPage(response.data.page);
        showNotification('Page created successfully');
      }
      await fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      showNotification(error.response?.data?.detail || 'Error saving page', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async () => {
    if (!editingPage?.id) return;
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendUrl}/api/cms/pages/${editingPage.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPage(null);
      setSelectedPage(null);
      await fetchPages();
      showNotification('Page deleted successfully');
    } catch (error) {
      console.error('Error deleting page:', error);
      showNotification(error.response?.data?.detail || 'Error deleting page', 'error');
    }
  };

  const generateContentFromUrl = async () => {
    if (!generatorUrl) return;
    
    // Capture the current page path at the start of generation
    const targetPagePath = selectedPage;
    
    setGeneratingContent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/ai/generate-content-from-url`,
        { url: generatorUrl, import_images: importImages, enable_web_research: enableWebResearch },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const generatedData = response.data.data;
        
        // Only update if we're still on the same page that started the generation
        setEditingPage(prev => {
          if (prev?.path !== targetPagePath) {
            console.warn('Page changed during AI generation. Discarding results for:', targetPagePath);
            showNotification('Page changed during generation. Please try again on the current page.', 'error');
            return prev;
          }
          
          // Build updated page data
          const updatedPage = {
            ...prev,
            title: generatedData.title || prev.title,
            badge_text: generatedData.badge_text || prev.badge_text,
            description: generatedData.description || prev.description,
            main_content: generatedData.main_content || prev.main_content,
            hero_image: generatedData.hero_image || prev.hero_image
          };
          
          // Auto-save the generated content
          (async () => {
            try {
              if (updatedPage.id) {
                await axios.put(`${backendUrl}/api/cms/pages/${updatedPage.id}`, updatedPage, {
                  headers: { Authorization: `Bearer ${token}` }
                });
              } else {
                const saveResponse = await axios.post(`${backendUrl}/api/cms/pages`, updatedPage, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                // Update with saved page data including ID
                setEditingPage(saveResponse.data.page);
              }
              await fetchPages();
              console.log('Auto-saved generated content');
            } catch (saveError) {
              console.error('Auto-save failed:', saveError);
              showNotification('Content generated but auto-save failed. Please save manually.', 'error');
            }
          })();
          
          return updatedPage;
        });
        
        showNotification('Content generated and saved!', 'success');
        setGeneratorUrl(''); // Clear the URL input
      }
    } catch (error) {
      console.error('Error generating content:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to generate content. Please check the URL and try again.',
        'error'
      );
    } finally {
      setGeneratingContent(false);
    }
  };

  // Generate content from raw text
  const generateContentFromRawText = async () => {
    if (!rawContent.trim()) return;
    
    // Capture the current page path at the start of generation
    const targetPagePath = selectedPage;
    
    setGeneratingFromRaw(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/ai/generate-content-from-text`,
        { raw_content: rawContent, enable_web_research: rawContentWebResearch },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const generatedData = response.data.data;
        
        // Only update if we're still on the same page that started the generation
        setEditingPage(prev => {
          if (prev?.path !== targetPagePath) {
            console.warn('Page changed during AI generation. Discarding results for:', targetPagePath);
            showNotification('Page changed during generation. Please try again on the current page.', 'error');
            return prev;
          }
          
          // Build updated page data
          const updatedPage = {
            ...prev,
            title: generatedData.title || prev.title,
            badge_text: generatedData.badge_text || prev.badge_text,
            description: generatedData.description || prev.description,
            main_content: generatedData.main_content || prev.main_content,
            hero_image: generatedData.hero_image || prev.hero_image
          };
          
          // Auto-save the generated content
          (async () => {
            try {
              if (updatedPage.id) {
                await axios.put(`${backendUrl}/api/cms/pages/${updatedPage.id}`, updatedPage, {
                  headers: { Authorization: `Bearer ${token}` }
                });
              } else {
                const saveResponse = await axios.post(`${backendUrl}/api/cms/pages`, updatedPage, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                // Update with saved page data including ID
                setEditingPage(saveResponse.data.page);
              }
              await fetchPages();
              console.log('Auto-saved generated content');
            } catch (saveError) {
              console.error('Auto-save failed:', saveError);
              showNotification('Content generated but auto-save failed. Please save manually.', 'error');
            }
          })();
          
          return updatedPage;
        });
        
        showNotification('Content generated and saved!', 'success');
        setRawContent('');
        setRawContentWebResearch(false);
        setShowRawContentModal(false);
      }
    } catch (error) {
      console.error('Error generating content from raw text:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to generate content. Please try again.',
        'error'
      );
    } finally {
      setGeneratingFromRaw(false);
    }
  };


  const openCreatePageModal = (parentPath = null) => {
    setNewPageParent(parentPath);
    setNewPageLayout('default');
    setShowCreatePageModal(true);
  };

  // Context menu handlers
  const handleTreeContextMenu = (e, node, forceIsFolder = false) => {
    e.preventDefault();
    
    // Calculate position to keep menu on screen
    const menuWidth = 220;
    const menuHeight = 250;
    let x = e.clientX;
    let y = e.clientY;
    
    // Adjust if menu would go off right edge
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    
    // Adjust if menu would go off bottom edge
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10;
    }
    
    // Check if node is a folder (has children OR forceIsFolder flag)
    const isFolder = forceIsFolder || (node.children && Object.keys(node.children).length > 0);
    
    setContextMenu({
      show: true,
      x: x,
      y: y,
      node: node,
      isFolder: isFolder
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, node: null, isFolder: false });
  };

  const handleCreateHomePage = () => {
    let parentPath = contextMenu.node?.path || '/';
    if (!parentPath.endsWith('/')) parentPath += '/';
    setNewPageParent(parentPath);
    setNewPageLayout('home');
    closeContextMenu();
    setShowCreatePageModal(true);
  };

  const handleCreateInnerPage = () => {
    let parentPath = contextMenu.node?.path || '/';
    if (!parentPath.endsWith('/')) parentPath += '/';
    setNewPageParent(parentPath);
    setNewPageLayout('default');
    closeContextMenu();
    setShowCreatePageModal(true);
  };

  const handleCreateFolder = () => {
    let parentPath = contextMenu.node?.path || '/';
    if (!parentPath.endsWith('/')) parentPath += '/';
    
    setFolderParentPath(parentPath);
    setFolderName('');
    closeContextMenu();
    setShowFolderModal(true);
  };

  // Create new folder at root level (Custom Pages)
  const handleCreateRootFolder = () => {
    setFolderParentPath('/');
    setFolderName('');
    setShowFolderModal(true);
  };

  // Submit folder creation - creates folder only (no page content)
  const handleFolderSubmit = async () => {
    if (!folderName.trim()) return;
    
    const cleanName = folderName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const folderPath = folderParentPath + cleanName;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/cms/folders`,
        {
          path: folderPath,
          title: folderName,
          parent_path: folderParentPath
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Folder created:', response.data);
      
      setShowFolderModal(false);
      setFolderName('');
      // Folder created silently - no notification needed
      
      // Refresh folders list
      await fetchPages();
      
      // Auto-scroll to show the newly created folder
      setTimeout(() => {
        if (treeContainerRef.current) {
          treeContainerRef.current.scrollTop = treeContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error creating folder:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to create folder',
        'error'
      );
    }
  };

  // Handle delete from context menu
  const handleDeleteFromContextMenu = () => {
    if (!contextMenu.node) return;
    
    const node = contextMenu.node;
    // Use contextMenu.isFolder which is explicitly set when opening the menu
    const isFolder = contextMenu.isFolder || (node.children && Object.keys(node.children).length > 0);
    
    console.log('Delete target:', { path: node.path, label: node.label, isFolder, contextMenuIsFolder: contextMenu.isFolder });
    
    setDeleteTarget({
      type: isFolder ? 'folder' : 'page',
      path: node.path,
      title: node.label || node.path
    });
    closeContextMenu();
    setShowDeleteModal(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    console.log('Confirming delete:', deleteTarget);
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (deleteTarget.type === 'folder') {
        // Delete folder by path
        const deletePath = deleteTarget.path;
        console.log('Deleting folder at path:', deletePath, 'Encoded:', encodeURIComponent(deletePath));
        await axios.delete(`${backendUrl}/api/cms/folders/by-path?path=${encodeURIComponent(deletePath)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Folder deleted silently
      } else {
        // Delete page by path
        const deletePath = deleteTarget.path;
        console.log('Deleting page at path:', deletePath, 'Encoded:', encodeURIComponent(deletePath));
        const deleteUrl = `${backendUrl}/api/cms/pages/by-path?path=${encodeURIComponent(deletePath)}`;
        console.log('Delete URL:', deleteUrl);
        await axios.delete(deleteUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Page deleted silently
      }
      
      // Clear selection if deleted item was selected
      if (selectedPage === deleteTarget.path) {
        setSelectedPage(null);
        setEditingPage(null);
      }
      
      await fetchPages();
    } catch (error) {
      console.error('Error deleting:', error);
      showNotification(
        error.response?.data?.detail || `Failed to delete ${deleteTarget.type}`,
        'error'
      );
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // Handle move from context menu
  const handleMoveFromContextMenu = () => {
    if (!contextMenu.node) return;
    
    const node = contextMenu.node;
    // Find the page to get its ID
    const page = pages.find(p => p.path === node.path);
    
    if (!page) {
      showNotification('Can only move custom pages', 'error');
      closeContextMenu();
      return;
    }
    
    setMoveTarget({
      id: page.id,
      path: node.path,
      title: node.label || node.path
    });
    setSelectedMoveFolder('/');
    closeContextMenu();
    setShowMoveModal(true);
  };

  // Handle edit from context menu
  const handleEditFromContextMenu = () => {
    if (!contextMenu.node) return;
    
    const node = contextMenu.node;
    selectPage(node.path);
    closeContextMenu();
  };

  // Confirm move
  const handleConfirmMove = async () => {
    if (!moveTarget) return;
    
    setMoving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${backendUrl}/api/cms/pages/move`, {
        page_id: moveTarget.id,
        target_folder_path: selectedMoveFolder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showNotification(`Page "${moveTarget.title}" moved successfully`, 'success');
      
      // Clear selection if moved item was selected
      if (selectedPage === moveTarget.path) {
        setSelectedPage(null);
        setEditingPage(null);
      }
      
      await fetchPages();
    } catch (error) {
      console.error('Error moving:', error);
      showNotification(
        error.response?.data?.detail || 'Failed to move page',
        'error'
      );
    } finally {
      setMoving(false);
      setShowMoveModal(false);
      setMoveTarget(null);
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        closeContextMenu();
      }
    };
    
    if (contextMenu.show) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.show]);

  const createNewPage = async (pageData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${backendUrl}/api/cms/pages`, {
        ...pageData,
        parent_path: newPageParent,
        is_custom: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreatePageModal(false);
      setNewPageParent(null);
      await fetchPages();
      selectPage(response.data.page.path);
      // Page created silently - no notification needed
      
      // Auto-scroll to show the newly created page
      setTimeout(() => {
        if (treeContainerRef.current) {
          treeContainerRef.current.scrollTop = treeContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      console.error('Error creating page:', error);
      showNotification(error.response?.data?.detail || 'Error creating page', 'error');
    }
  };

  // Theme CRUD operations
  const openNewThemeModal = () => {
    setEditingTheme({
      name: '',
      description: '',
      colors: { ...defaultColors },
      is_active: false
    });
    setShowThemeModal(true);
  };

  const openEditThemeModal = (theme) => {
    setEditingTheme({ ...theme, colors: { ...defaultColors, ...theme.colors } });
    setShowThemeModal(true);
  };

  const closeThemeModal = () => {
    setShowThemeModal(false);
    setEditingTheme(null);
  };

  const handleColorChange = (key, value) => {
    const rgbValue = hexToRgb(value);
    
    // If primary color changes, auto-derive all other colors
    if (key === 'primary') {
      const derivedColors = generateColorsFromPrimary(rgbValue);
      setEditingTheme(prev => ({
        ...prev,
        colors: derivedColors
      }));
    } else {
      // For other colors, just update that specific color
      setEditingTheme(prev => ({
        ...prev,
        colors: {
          ...prev.colors,
          [key]: rgbValue
        }
      }));
    }
  };

  const resetToOriginalColors = () => {
    setEditingTheme(prev => ({
      ...prev,
      colors: { ...originalDefaultColors }
    }));
    showNotification('Colors reset to original', 'success');
  };

  const saveTheme = async () => {
    if (!editingTheme.name.trim()) {
      showNotification('Please enter a theme name', 'error');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingTheme.id) {
        // Update existing theme
        await axios.put(`${backendUrl}/api/website-themes/${editingTheme.id}`, editingTheme, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Theme updated successfully');
      } else {
        // Create new theme
        await axios.post(`${backendUrl}/api/website-themes`, editingTheme, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Theme created successfully');
      }
      
      closeThemeModal();
      fetchThemes();
      
      // Refresh global theme if this one is active
      if (editingTheme.is_active) {
        refreshTheme();
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      showNotification(error.response?.data?.detail || 'Error saving theme', 'error');
    } finally {
      setSaving(false);
    }
  };

  const activateTheme = async (themeId) => {
    setApplyingTheme(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${backendUrl}/api/website-themes/${themeId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshTheme(); // Update global theme
      await fetchThemes();
      // Small delay to show the animation completing
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error activating theme:', error);
      showNotification(error.response?.data?.detail || 'Error activating theme', 'error');
    } finally {
      setApplyingTheme(false);
    }
  };

  const deleteTheme = async (themeId) => {
    if (!window.confirm('Are you sure you want to delete this theme?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendUrl}/api/website-themes/${themeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Theme deleted successfully');
      fetchThemes();
    } catch (error) {
      console.error('Error deleting theme:', error);
      showNotification(error.response?.data?.detail || 'Error deleting theme', 'error');
    }
  };

  const duplicateTheme = (theme) => {
    // Create a copy of the theme with a new name
    setEditingTheme({
      name: `${theme.name} Copy`,
      description: theme.description || '',
      colors: { ...theme.colors },
      is_active: false
    });
    setShowThemeModal(true);
  };

  // Check if theme is the default Ocean Blue (not editable/deletable)
  const isDefaultTheme = (themeName) => {
    return themeName === 'Ocean Blue';
  };

  // Filter items based on search
  const filteredThemes = themes.filter(theme =>
    theme.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    theme.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTemplates = templates.filter(template =>
    template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPages = pages.filter(page =>
    page.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Content Manager</h1>
            
            {/* Search and Add Button - Right Side */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48 pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={activeTab === 'themes' ? openNewThemeModal : activeTab === 'components' ? openNewComponentModal : undefined}
                className="px-3 py-1.5 bg-theme-blue text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add {activeTab === 'themes' ? 'Theme' : activeTab === 'templates' ? 'Template' : activeTab === 'components' ? 'Component' : 'Page'}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => { setActiveTab('themes'); setSearchTerm(''); }}
                className={`py-3 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'themes'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Palette className="h-4 w-4 inline mr-2" />
                Themes
              </button>
              <button
                onClick={() => { setActiveTab('templates'); setSearchTerm(''); }}
                className={`py-3 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'templates'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Layout className="h-4 w-4 inline mr-2" />
                Templates
              </button>
              <button
                onClick={() => { setActiveTab('pages'); setSearchTerm(''); }}
                className={`py-3 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'pages'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                Pages
              </button>
              <button
                onClick={() => { setActiveTab('components'); setSearchTerm(''); }}
                className={`py-3 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'components'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Layers className="h-4 w-4 inline mr-2" />
                Components
              </button>
              
              {/* Full Page View Button - Only show when on Pages tab */}
              {activeTab === 'pages' && (
                <button
                  onClick={() => navigate('/cms-editor')}
                  className="ml-auto py-2 px-4 text-sm font-medium text-gray-600 hover:text-theme-blue hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
                  title="Open Full Page Editor"
                >
                  <Maximize2 className="h-4 w-4" />
                  Full Page View
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'themes' ? (
          // Themes Tab
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Themes List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading themes...</p>
              </div>
            ) : filteredThemes.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Palette className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No themes found</p>
                <p className="text-gray-400 text-sm mt-1">Create your first theme to customize website colors</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredThemes.map((theme) => (
                  <div 
                    key={theme.id} 
                    className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                      theme.is_active ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{theme.name}</h3>
                          {isDefaultTheme(theme.name) && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Default</span>
                          )}
                          {theme.is_active && (
                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">Active</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{theme.description || 'No description'}</p>
                      </div>
                    </div>
                    
                    {/* Color Preview */}
                    <div className="flex gap-1 mb-4">
                      {['primary', 'secondary', 'accent', 'button_bg', 'gradient_start', 'gradient_end'].map(colorKey => (
                        <div
                          key={colorKey}
                          className="w-6 h-6 rounded-full border border-gray-300"
                          style={{ backgroundColor: theme.colors?.[colorKey] || defaultColors[colorKey] }}
                          title={colorKey}
                        />
                      ))}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center justify-between">
                      {/* Set Active Button */}
                      {theme.is_active ? (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <Check className="h-3.5 w-3.5" />
                          Currently Active
                        </span>
                      ) : (
                        <button 
                          onClick={() => activateTheme(theme.id)}
                          className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                        >
                          Set Active
                        </button>
                      )}
                      
                      {/* Duplicate, Edit & Delete */}
                      <div className="flex items-center gap-1">
                        {/* Duplicate - available for all themes */}
                        <button 
                          onClick={() => duplicateTheme(theme)}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                          title="Duplicate Theme"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        
                        {/* Edit - not available for Ocean Blue */}
                        {!isDefaultTheme(theme.name) && (
                          <button 
                            onClick={() => openEditThemeModal(theme)}
                            className="p-1.5 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded"
                            title="Edit Theme"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Delete - not available for Ocean Blue or active theme */}
                        {!isDefaultTheme(theme.name) && !theme.is_active && (
                          <button 
                            onClick={() => deleteTheme(theme.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete Theme"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'templates' ? (
          // Templates Tab
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Group templates by page type */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading templates...</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Layout className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No templates found</p>
                <p className="text-gray-400 text-sm mt-1">Create your first template to get started</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Home Page Templates */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Layout className="h-4 w-4" />
                    Home Page Layouts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTemplates
                      .filter(t => t.page_type === 'home')
                      .map((template) => (
                        <div 
                          key={template.id} 
                          className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                            template.is_active ? 'border-green-500 bg-green-50' : 'border-gray-200'
                          }`}
                        >
                          {/* Template Preview */}
                          <div className="mb-3 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                            {template.layout === 'default' ? (
                              <div className="w-full h-full p-2 flex gap-2">
                                <div className="w-1/2 bg-gray-300 rounded flex items-center justify-center text-xs text-gray-500">
                                  Content
                                </div>
                                <div className="w-1/2 bg-gray-400 rounded flex items-center justify-center text-xs text-white">
                                  Hero Image
                                </div>
                              </div>
                            ) : template.layout === 'full_width_hero' ? (
                              <div className="w-full h-full p-2 flex flex-col gap-2">
                                <div className="flex-1 bg-gray-400 rounded flex items-center justify-center text-xs text-white">
                                  Full Width Hero
                                </div>
                                <div className="h-8 bg-gray-300 rounded flex items-center justify-center text-xs text-gray-500">
                                  Content
                                </div>
                              </div>
                            ) : (
                              <Layout className="h-8 w-8 text-gray-400" />
                            )}
                          </div>
                          
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900">{template.name}</h4>
                                {template.is_default && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Default</span>
                                )}
                                {template.is_active && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">Active</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">{template.description || 'No description'}</p>
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                            {template.is_active ? (
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                <Check className="h-3.5 w-3.5" />
                                Currently Active
                              </span>
                            ) : (
                              <button 
                                onClick={() => activateTemplate(template.id)}
                                className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                              >
                                Set Active
                              </button>
                            )}
                            
                            <div className="flex items-center gap-1">
                              <button 
                                className="p-1.5 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded"
                                title="Preview"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Inner Page Layouts Section */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Inner Page Layouts
                    </h3>
                    <button
                      onClick={openNewInnerTemplateModal}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-theme-blue hover:bg-blue-600 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Layout
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredTemplates
                      .filter(t => t.page_type === 'inner')
                      .map((template) => (
                        <div 
                          key={template.id} 
                          className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                            template.is_active ? 'border-green-500 bg-green-50' : 'border-gray-200'
                          }`}
                        >
                          {/* Template Preview - Image Right Layout */}
                          <div className="mb-3 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                            <div className="w-full h-full p-2 flex flex-col gap-1">
                              <div className="flex-1 flex gap-2">
                                <div className="w-1/2 bg-gray-300 rounded flex flex-col items-start justify-center p-2">
                                  <div className="w-3/4 h-2 bg-gray-400 rounded mb-1"></div>
                                  <div className="w-full h-1.5 bg-gray-400/50 rounded mb-1"></div>
                                  <div className="w-2/3 h-1.5 bg-gray-400/50 rounded"></div>
                                </div>
                                <div className="w-1/2 bg-theme-primary/30 rounded flex items-center justify-center text-xs text-gray-600">
                                  Image
                                </div>
                              </div>
                              <div className="h-8 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                                Page Content
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900">{template.name}</h4>
                                {template.is_active && (
                                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">Active</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">{template.description || 'No description'}</p>
                            </div>
                          </div>
                          
                          {/* Applied Pages */}
                          {template.applied_pages && template.applied_pages.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-1">Applied to:</p>
                              <div className="flex flex-wrap gap-1">
                                {template.applied_pages.slice(0, 3).map(page => (
                                  <span key={page} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                    {availablePublicPages.find(p => p.path === page)?.label || page}
                                  </span>
                                ))}
                                {template.applied_pages.length > 3 && (
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                    +{template.applied_pages.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Actions */}
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                            {template.is_active ? (
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                <Check className="h-3.5 w-3.5" />
                                Currently Active
                              </span>
                            ) : (
                              <button 
                                onClick={() => activateTemplate(template.id)}
                                className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                              >
                                Set Active
                              </button>
                            )}
                            
                            <div className="flex items-center gap-1">
                              <button 
                                onClick={() => openEditInnerTemplateModal(template)}
                                className="p-1.5 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => deleteInnerTemplate(template.id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    
                    {/* Empty state for inner templates */}
                    {filteredTemplates.filter(t => t.page_type === 'inner').length === 0 && (
                      <div className="col-span-2 text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                        <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">No inner page layouts yet</p>
                        <p className="text-gray-400 text-xs mt-1">Create a layout to apply to your public pages</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'pages' ? (
          // Pages Tab - Full CMS
          <div className="flex gap-6 h-[calc(100vh-220px)]">
            {/* Left Panel - Tree Navigation */}
            <div className="w-80 flex-shrink-0 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Pages</h3>
                <button
                  onClick={() => openCreatePageModal()}
                  className="p-1.5 text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                  title="Create new page"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              
              <div 
                ref={treeContainerRef}
                className="flex-1 overflow-y-auto p-2 scrollbar-thin"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }}
              >
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-blue mx-auto"></div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Render tree structure */}
                    {Object.entries(pageStructure).map(([key, node]) => (
                      <TreeNode
                        key={key}
                        nodeKey={key}
                        node={node}
                        level={0}
                        expandedNodes={expandedNodes}
                        toggleNode={toggleNode}
                        selectedPage={selectedPage}
                        selectPage={selectPage}
                        pages={pages}
                        folders={folders}
                        openCreatePageModal={openCreatePageModal}
                        onContextMenu={handleTreeContextMenu}
                      />
                    ))}
                    
                    {/* Custom pages and folders section */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="px-2 py-1 flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Custom Pages
                        </span>
                        <button
                          onClick={handleCreateRootFolder}
                          className="p-1 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded transition-colors"
                          title="Create New Folder"
                        >
                          <FolderPlus className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Render custom folders with their pages */}
                      {folders.length > 0 && folders.map(folder => {
                        const folderPages = pages.filter(p => {
                          if (!p.is_custom) return false;
                          // Normalize paths by removing trailing slashes for comparison
                          const pageParentPath = (p.parent_path || '').replace(/\/+$/, '');
                          const folderPath = (folder.path || '').replace(/\/+$/, '');
                          return pageParentPath === folderPath;
                        });
                        const isFolderExpanded = expandedNodes.has(`custom-${folder.id}`);
                        
                        return (
                          <div key={folder.id}>
                            <div
                              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg text-gray-700 hover:bg-gray-100 cursor-pointer"
                              onClick={(e) => toggleNode(`custom-${folder.id}`, e.currentTarget)}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                handleTreeContextMenu(e, { path: folder.path, label: folder.title }, true);
                              }}
                            >
                              {folderPages.length > 0 ? (
                                isFolderExpanded ? (
                                  <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                )
                              ) : (
                                <span className="w-3.5" />
                              )}
                              {isFolderExpanded ? (
                                <FolderOpen className="h-4 w-4 flex-shrink-0 text-yellow-500" />
                              ) : (
                                <Folder className="h-4 w-4 flex-shrink-0 text-yellow-500" />
                              )}
                              <span className="truncate">{folder.title}</span>
                              {folderPages.length > 0 && (
                                <span className="text-xs text-gray-400 ml-auto">{folderPages.length}</span>
                              )}
                            </div>
                            
                            {/* Pages inside this folder */}
                            {isFolderExpanded && folderPages.map(page => (
                              <div
                                key={page.id}
                                onClick={() => selectPage(page.path)}
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  handleTreeContextMenu(e, { path: page.path, label: page.title }, false);
                                }}
                                className={`w-full flex items-center gap-2 pl-10 pr-3 py-2 text-left text-sm rounded-lg transition-colors cursor-pointer ${
                                  selectedPage === page.path
                                    ? 'bg-theme-blue text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                <File className={`h-4 w-4 flex-shrink-0 ${selectedPage === page.path ? 'text-white' : 'text-gray-400'}`} />
                                <span className="truncate">{page.title}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                      
                      {/* Render custom pages at root level (not in any folder) */}
                      {pages.filter(p => p.is_custom && (!p.parent_path || p.parent_path === '/')).map(page => (
                        <div
                          key={page.id}
                          onClick={() => selectPage(page.path)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            handleTreeContextMenu(e, { path: page.path, label: page.title }, false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition-colors cursor-pointer ${
                            selectedPage === page.path
                              ? 'bg-theme-blue text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <File className={`h-4 w-4 flex-shrink-0 ${selectedPage === page.path ? 'text-white' : 'text-gray-400'}`} />
                          <span className="truncate">{page.title}</span>
                        </div>
                      ))}
                      
                      {/* Show message if no custom content */}
                      {folders.length === 0 && pages.filter(p => p.is_custom).length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-400 italic">
                          No custom pages yet. Click + to create a folder.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Page Editor */}
            <div className="flex-1 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
              {selectedPage && editingPage ? (
                <>
                  {/* Editor Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex-1 mr-4">
                      <h3 className="font-semibold text-gray-900">{editingPage.title || 'Edit Page'}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">Path:</span>
                        <input
                          type="text"
                          value={editingPage.path || ''}
                          onChange={(e) => {
                            let newPath = e.target.value;
                            if (newPath && !newPath.startsWith('/')) {
                              newPath = '/' + newPath;
                            }
                            setEditingPage({...editingPage, path: newPath});
                          }}
                          className="flex-1 max-w-xs px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                          placeholder="/page-slug"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/cms-editor?page=${encodeURIComponent(editingPage.path)}`)}
                        className="p-2 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="Full Page View"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </button>
                      <a
                        href={`${editingPage.path}?preview=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-theme-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="Preview page"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      {editingPage.is_custom && (
                        <button
                          onClick={deletePage}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete page"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={savePage}
                        disabled={saving}
                        className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {saving ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Save
                      </button>
                    </div>
                  </div>

                  {/* Editor Content */}
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-3xl mx-auto space-y-6">
                      {/* AI Content Generator from URL */}
                      <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="h-5 w-5 text-blue-600" />
                          <h4 className="font-semibold text-gray-900">AI Content Generator</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Generate page content from any website URL using AI. The system will scrape the website and create beautiful content.
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={generatorUrl || ''}
                            onChange={(e) => setGeneratorUrl(e.target.value)}
                            placeholder="https://example.com/page-to-scrape"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={generatingContent}
                          />
                          <button
                            onClick={generateContentFromUrl}
                            disabled={!generatorUrl || generatingContent}
                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                          >
                            {generatingContent ? (
                              <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4" />
                                Generate Content
                              </>
                            )}
                          </button>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={importImages}
                              onChange={(e) => setImportImages(e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              disabled={generatingContent}
                            />
                            <ImagePlus className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-600">Import images to S3</span>
                          </label>
                          
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={enableWebResearch}
                              onChange={(e) => setEnableWebResearch(e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              disabled={generatingContent}
                            />
                            <Globe className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-600">Enhance with web research</span>
                          </label>
                          
                          {/* Raw Content Button */}
                          <button
                            onClick={() => setShowRawContentModal(true)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 ml-auto"
                          >
                            <FileText className="h-4 w-4" />
                            Paste Raw Content
                          </button>
                        </div>
                        {generatingContent && (
                          <div className="text-xs text-blue-700 animate-pulse">
                            ✨ Scraping website and generating content with OpenAI + Claude...
                          </div>
                        )}
                      </div>

                      {/* Basic Info */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Page Information
                        </h4>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              type="text"
                              value={editingPage.title || ''}
                              onChange={(e) => setEditingPage({...editingPage, title: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                              placeholder="Page title"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                            <input
                              type="text"
                              value={editingPage.badge_text || ''}
                              onChange={(e) => setEditingPage({...editingPage, badge_text: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                              placeholder="e.g., Professional Care"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={editingPage.description || ''}
                            onChange={(e) => setEditingPage({...editingPage, description: e.target.value})}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                            placeholder="Brief description for the hero section"
                          />
                          <div className="mt-2">
                            <CMSImagePicker
                              value={editingPage.hero_image || ''}
                              onChange={(url) => setEditingPage({...editingPage, hero_image: url})}
                              showInline={true}
                              buttonLabel="Add Hero Image"
                            />
                          </div>
                        </div>
                        
                        {/* Show hero image preview if set */}
                        {editingPage.hero_image && (
                          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                            <div className="flex items-center gap-4">
                              <img 
                                src={editingPage.hero_image} 
                                alt="Hero" 
                                className="w-24 h-16 object-cover rounded-lg border border-gray-200" 
                              />
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 mb-2">Hero Image</p>
                                <div className="flex gap-1">
                                  {['square', 'landscape', 'portrait'].map((aspect) => (
                                    <button
                                      key={aspect}
                                      type="button"
                                      onClick={() => setEditingPage({...editingPage, hero_image_aspect: aspect})}
                                      className={`px-2 py-1 text-xs rounded capitalize ${
                                        (editingPage.hero_image_aspect || 'landscape') === aspect
                                          ? 'bg-blue-500 text-white'
                                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                      }`}
                                    >
                                      {aspect}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <button
                                onClick={() => setEditingPage({...editingPage, hero_image: '', hero_image_aspect: 'landscape'})}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Image Position */}
                      <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Layout className="h-4 w-4" />
                          Image Position
                        </h4>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image Position</label>
                            <select
                              value={editingPage.layout || 'no_image'}
                              onChange={(e) => setEditingPage({...editingPage, layout: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                            >
                              <option value="no_image">No Image</option>
                              <option value="image_right">Image on Right</option>
                              <option value="image_left">Image on Left</option>
                              <option value="image_top">Top Section</option>
                            </select>
                          </div>
                          <div>
                            <label className="flex items-center gap-2 cursor-pointer mt-6">
                              <input
                                type="checkbox"
                                checked={editingPage.is_published || false}
                                onChange={(e) => setEditingPage({...editingPage, is_published: e.target.checked})}
                                className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                              />
                              <span className="text-sm text-gray-700">Published</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Page Content Editor */}
                      <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Type className="h-4 w-4" />
                          Main Content
                        </h4>
                        
                        {/* Check if content is structured JSON and render inline editor */}
                        {(() => {
                          try {
                            const parsed = editingPage.main_content 
                              ? (typeof editingPage.main_content === 'string' 
                                ? JSON.parse(editingPage.main_content) 
                                : editingPage.main_content)
                              : null;
                            
                            if (parsed && parsed.sections && Array.isArray(parsed.sections)) {
                              return (
                                <InlineStructuredContentEditor
                                  content={editingPage.main_content}
                                  onChange={(updatedContent) => setEditingPage({...editingPage, main_content: updatedContent})}
                                />
                              );
                            }
                            return null;
                          } catch {
                            return null;
                          }
                        })() || (
                          <>
                            <p className="text-xs text-gray-500">
                              Edit the main content area below the hero section. Use the visual editor for formatting or switch to HTML mode for code editing.
                            </p>
                            <RichTextEditor
                              value={editingPage.main_content || ''}
                              onChange={(value) => setEditingPage({...editingPage, main_content: value})}
                              placeholder="Enter the main page content here. This will replace the default content below the hero section."
                              rows={10}
                            />
                          </>
                        )}
                      </div>

                      {/* Page Components Section */}
                      <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Layers className="h-4 w-4" />
                          Page Components
                        </h4>
                        <p className="text-xs text-gray-500">
                          Add reusable components that will appear below the main content section. Components will be displayed in the order shown.
                        </p>
                        
                        {/* Component List */}
                        <div className="space-y-2">
                          {editingPage.components && editingPage.components.length > 0 ? (
                            editingPage.components.map((pageComponent, index) => {
                              // Support both old format (string ID) and new format (object with component_id)
                              const componentId = typeof pageComponent === 'string' ? pageComponent : pageComponent.component_id;
                              const componentConfig = typeof pageComponent === 'object' ? pageComponent.config : {};
                              const component = components.find(c => c.id === componentId);
                              return (
                                <div 
                                  key={index}
                                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                                >
                                  <div className="flex items-center gap-2 cursor-move text-gray-400">
                                    <GripVertical className="h-4 w-4" />
                                    <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                                  </div>
                                  
                                  <div className="flex-1">
                                    {component ? (
                                      <>
                                        <p className="text-sm font-medium text-gray-900">
                                          {componentConfig?.title || component.name}
                                        </p>
                                        <p className="text-xs text-gray-500">{component.type}</p>
                                        {componentConfig && Object.keys(componentConfig).length > 0 && (
                                          <p className="text-xs text-blue-600 mt-0.5">Custom config applied</p>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-sm text-red-600">Component not found</p>
                                    )}
                                  </div>
                                  
                                  <div className="flex items-center gap-1">
                                    {/* Edit Component Config Button */}
                                    {component && (
                                      <button
                                        onClick={() => {
                                          setEditingPageComponent({
                                            component_id: componentId,
                                            config: { ...component.config, ...componentConfig },
                                            baseComponent: component
                                          });
                                          setEditingPageComponentIndex(index);
                                          setShowPageComponentModal(true);
                                        }}
                                        className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        title="Edit Component Settings"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                    )}
                                    {index > 0 && (
                                      <button
                                        onClick={() => {
                                          const newComponents = [...editingPage.components];
                                          [newComponents[index], newComponents[index - 1]] = [newComponents[index - 1], newComponents[index]];
                                          setEditingPage({...editingPage, components: newComponents});
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                                        title="Move Up"
                                      >
                                        <ChevronRight className="h-4 w-4 rotate-[-90deg]" />
                                      </button>
                                    )}
                                    {index < editingPage.components.length - 1 && (
                                      <button
                                        onClick={() => {
                                          const newComponents = [...editingPage.components];
                                          [newComponents[index], newComponents[index + 1]] = [newComponents[index + 1], newComponents[index]];
                                          setEditingPage({...editingPage, components: newComponents});
                                        }}
                                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
                                        title="Move Down"
                                      >
                                        <ChevronRight className="h-4 w-4 rotate-90" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => {
                                        const newComponents = editingPage.components.filter((_, i) => i !== index);
                                        setEditingPage({...editingPage, components: newComponents});
                                      }}
                                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Remove"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                              <Layers className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">No components added yet</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Add Component Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Add Component</label>
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                const selectedComponent = components.find(c => c.id === e.target.value);
                                const newPageComponent = {
                                  component_id: e.target.value,
                                  config: {} // Start with empty config, will inherit from base component
                                };
                                const newComponents = [...(editingPage.components || []), newPageComponent];
                                setEditingPage({...editingPage, components: newComponents});
                                
                                // Open edit modal for the newly added component (except for reviews which is configured globally)
                                if (selectedComponent && selectedComponent.type !== 'reviews') {
                                  setEditingPageComponent({
                                    component_id: e.target.value,
                                    config: { ...selectedComponent.config },
                                    baseComponent: selectedComponent
                                  });
                                  setEditingPageComponentIndex(newComponents.length - 1);
                                  setShowPageComponentModal(true);
                                }
                                
                                e.target.value = ''; // Reset dropdown
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                          >
                            <option value="">Select a component to add...</option>
                            {components.filter(c => c.is_published).map(component => (
                              <option key={component.id} value={component.id}>
                                {component.name} ({component.type})
                              </option>
                            ))}
                          </select>
                          {components.filter(c => c.is_published).length === 0 && (
                            <p className="text-xs text-amber-600 mt-1">
                              No published components available. Create components in the Components tab first.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* SEO */}
                      <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          SEO Settings
                        </h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                          <input
                            type="text"
                            value={editingPage.meta_title || ''}
                            onChange={(e) => setEditingPage({...editingPage, meta_title: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                            placeholder="SEO title (leave empty to use page title)"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                          <textarea
                            value={editingPage.meta_description || ''}
                            onChange={(e) => setEditingPage({...editingPage, meta_description: e.target.value})}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                            placeholder="SEO description for search engines"
                          />
                        </div>
                      </div>

                      {/* Page Info */}
                      {editingPage.id && (
                        <div className="pt-4 border-t border-gray-200 text-sm text-gray-500">
                          <p>Created: {editingPage.created_at ? new Date(editingPage.created_at).toLocaleString() : 'N/A'}</p>
                          <p>Last updated: {editingPage.updated_at ? new Date(editingPage.updated_at).toLocaleString() : 'N/A'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Select a page from the tree to edit</p>
                    <p className="text-gray-400 text-sm mt-1">Or create a new custom page</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'components' ? (
          // Components Tab
          <div className="bg-white rounded-xl shadow-sm p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading components...</p>
              </div>
            ) : (
              <>
                {/* Header Component - Special Global Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Global Header</h3>
                      <p className="text-sm text-gray-500">Configure your website header (appears on all pages)</p>
                    </div>
                  </div>
                  
                  {(() => {
                    const headerComponent = components.find(c => c.type === 'header' && c.slug === 'global-header');
                    return headerComponent ? (
                      <div className="border-2 border-purple-200 rounded-xl p-4 bg-purple-50/30">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 flex items-center gap-2">
                              {headerComponent.name}
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">Global</span>
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">Manage layout, menus, theme colors, and CTA buttons</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-3 border-t border-purple-200">
                          <button
                            onClick={() => openEditComponentModal(headerComponent)}
                            className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            Configure Header
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.post(
                                  `${backendUrl}/api/header-config/initialize`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                
                                if (response.data.success) {
                                  setNotification({ show: true, message: 'Header initialized with default settings', type: 'success' });
                                  setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
                                  fetchComponents();
                                }
                              } catch (error) {
                                console.error('Error initializing header:', error);
                                setNotification({ 
                                  show: true, 
                                  message: error.response?.data?.detail || 'Failed to initialize header', 
                                  type: 'error' 
                                });
                                setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
                              }
                            }}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Reset to Defaults
                          </button>
                          <span className="text-xs text-gray-600">
                            Cannot be deleted • Auto-displays on all pages
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                        <p className="text-gray-500 mb-3">Header component not found. Click below to initialize with default settings.</p>
                        <button
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.post(
                                `${backendUrl}/api/header-config/initialize`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              
                              if (response.data.success) {
                                setNotification({ show: true, message: 'Header component created', type: 'success' });
                                setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
                                fetchComponents();
                              }
                            } catch (error) {
                              console.error('Error creating header:', error);
                              setNotification({ 
                                show: true, 
                                message: error.response?.data?.detail || 'Failed to create header', 
                                type: 'error' 
                              });
                              setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
                            }
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          Initialize Header
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* Footer Component - Special Global Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Global Footer</h3>
                      <p className="text-sm text-gray-500">Configure your website footer (appears on all pages)</p>
                    </div>
                  </div>
                  
                  {(() => {
                    const footerComponent = components.find(c => c.type === 'footer' && c.slug === 'global-footer');
                    return footerComponent ? (
                      <div className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50/30">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 flex items-center gap-2">
                              {footerComponent.name}
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">Global</span>
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">Manage quick links, social media, hours, and legal links</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 pt-3 border-t border-blue-200">
                          <button
                            onClick={() => openEditComponentModal(footerComponent)}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            Configure Footer
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                console.log('Populating footer defaults...');
                                console.log('Token:', token ? 'Token exists' : 'No token');
                                console.log('Backend URL:', backendUrl);
                                
                                const response = await axios.post(
                                  `${backendUrl}/api/footer-config/populate-defaults`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                
                                console.log('Response:', response.data);
                                
                                if (response.data.success) {
                                  setNotification({ show: true, message: 'Footer populated with default links', type: 'success' });
                                  setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
                                  fetchComponents();
                                }
                              } catch (error) {
                                console.error('Error populating footer defaults:', error);
                                console.error('Error response:', error.response?.data);
                                setNotification({ 
                                  show: true, 
                                  message: error.response?.data?.detail || 'Failed to populate defaults', 
                                  type: 'error' 
                                });
                                setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
                              }
                            }}
                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Populate Defaults
                          </button>
                          <span className="text-xs text-gray-600">
                            Cannot be deleted • Auto-displays on all pages
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                        <p className="text-gray-500 mb-3">Footer component not found. Click below to auto-initialize with default settings.</p>
                        <button
                          onClick={async () => {
                            // Auto-create footer component with defaults
                            try {
                              const response = await axios.post(
                                `${backendUrl}/api/cms/components`,
                                {
                                  type: 'footer',
                                  name: 'Website Footer',
                                  slug: 'global-footer',
                                  description: 'Global footer configuration',
                                  is_published: true,
                                  config: {
                                    show_contact_info: true,
                                    show_address: true,
                                    show_phone: true,
                                    show_email: true,
                                    show_social_media: true,
                                    use_business_info_social: true, // Pull from business info
                                    show_hours: true,
                                    show_first_business_hours: true,
                                    show_second_business_hours: true,
                                    show_quick_links: true,
                                    show_legal_links: true,
                                    copyright_text: `© ${new Date().getFullYear()} All Rights Reserved`,
                                    quick_links: [
                                      { label: 'Our Mission', url: '/our-mission' },
                                      { label: 'Our Values', url: '/our-core-values' },
                                      { label: 'Our Team', url: '/our-team' },
                                      { label: 'Our Facility', url: '/gallery' },
                                      { label: 'Review Us', url: '/review-us' },
                                      { label: 'Your First Visit', url: '/your-first-visit' },
                                      { label: 'Insurance & Payments', url: '/pet-insurance-payments' },
                                      { label: 'Download Forms', url: '/forms-download' }
                                    ],
                                    legal_links: [
                                      { label: 'Terms of Service', url: '/terms' },
                                      { label: 'Privacy Policy', url: '/privacy' },
                                      { label: 'Accessibility', url: '/accessibility' }
                                    ],
                                    social_media: []
                                  }
                                },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              
                              if (response.data.success) {
                                showMessage('success', 'Footer initialized successfully');
                                fetchComponents();
                              }
                            } catch (error) {
                              showMessage('error', 'Failed to initialize footer');
                              console.error('Error initializing footer:', error);
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Auto-Initialize Footer
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* Regular Components Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Page Components</h3>
                      <p className="text-sm text-gray-500">Reusable components that can be added to pages</p>
                    </div>
                    <button
                      onClick={openNewComponentModal}
                      className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Component
                    </button>
                  </div>

                  {components.filter(c => c.type !== 'footer').length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                      <Layers className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Components Yet</h3>
                      <p className="text-gray-500 mb-4">Create reusable components like CTA sections, reviews, and more</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {components.filter(c => c.type !== 'footer').map((component) => (
                  <div 
                    key={component.id} 
                    className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-gray-900">{component.name}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{component.slug}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        component.is_published 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {component.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded">
                        {component.type === 'cta_section' && 'CTA Section'}
                        {component.type === 'dual_button_cta' && 'Dual Button CTA'}
                        {component.type === 'card_section' && 'Card Section'}
                        {component.type === 'home_hero' && 'Home Hero Section'}
                        {component.type === 'product_listing' && 'Product / Service Listing'}
                        {component.type === 'reviews' && 'Reviews'}
                        {component.type === 'service_areas' && 'Service Areas'}
                        {component.type === 'contact_info' && 'Contact Info'}
                        {component.type === 'footer' && 'Global Footer'}
                        {component.type === 'custom' && 'Custom'}
                      </span>
                    </div>
                    
                    {component.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{component.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => openEditComponentModal(component)}
                        className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-1"
                      >
                        <Edit className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => deleteComponent(component.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* Applying Theme Modal */}
      {applyingTheme && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl px-6 py-4 flex items-center gap-3">
            <svg className="animate-spin h-5 w-5 text-theme-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium text-gray-700">Applying theme...</span>
          </div>
        </div>
      )}

      {/* Theme Edit Modal */}
      {showThemeModal && editingTheme && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTheme.id ? 'Edit Theme' : 'Create New Theme'}
              </h2>
              <button onClick={closeThemeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Theme Name & Description */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Theme Name *</label>
                  <input
                    type="text"
                    value={editingTheme.name}
                    onChange={(e) => setEditingTheme(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Ocean Blue, Forest Green"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={editingTheme.description}
                    onChange={(e) => setEditingTheme(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this theme"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Color Fields */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Theme Colors</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Change Primary Color to auto-update all colors</p>
                  </div>
                  <button
                    onClick={resetToOriginalColors}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1.5"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Reset to Original
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {colorFields.map(({ key, label, description }) => (
                    <div key={key} className={`p-3 rounded-lg ${key === 'primary' ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          {label}
                          {key === 'primary' && <span className="ml-1 text-xs text-blue-600">(Master)</span>}
                        </label>
                        <span className="text-xs font-mono text-gray-500 bg-white px-2 py-0.5 rounded border">
                          {rgbToHex(editingTheme.colors?.[key])}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{description}</p>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-full h-10 rounded-lg border border-gray-300 cursor-pointer relative overflow-hidden"
                          style={{ backgroundColor: editingTheme.colors?.[key] || defaultColors[key] }}
                          onClick={() => document.getElementById(`color-${key}`).click()}
                        >
                          <input
                            id={`color-${key}`}
                            type="color"
                            value={rgbToHex(editingTheme.colors?.[key])}
                            onChange={(e) => handleColorChange(key, e.target.value)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium" 
                            style={{ color: isLightColor(editingTheme.colors?.[key]) ? '#333' : '#fff' }}>
                            Click to change
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview Section */}
              <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
                <div className="space-y-3">
                  <button
                    className="px-4 py-2 text-white rounded-lg"
                    style={{ backgroundColor: editingTheme.colors?.button_bg }}
                  >
                    Sample Button
                  </button>
                  <div 
                    className="h-12 rounded-lg flex items-center justify-center text-white font-medium"
                    style={{ background: `linear-gradient(to right, ${editingTheme.colors?.gradient_start}, ${editingTheme.colors?.gradient_end})` }}
                  >
                    Gradient Header
                  </div>
                  <div className="flex gap-2">
                    <a 
                      href="#" 
                      onClick={(e) => e.preventDefault()}
                      style={{ color: editingTheme.colors?.link_color }}
                      className="text-sm underline"
                    >
                      Sample Link
                    </a>
                    <span className="text-sm" style={{ color: editingTheme.colors?.accent }}>
                      Accent Text
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeThemeModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={saveTheme}
                disabled={saving}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Theme
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inner Page Template Modal */}
      {showInnerTemplateModal && editingInnerTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingInnerTemplate.id ? 'Edit Inner Page Layout' : 'Create Inner Page Layout'}
              </h2>
              <button onClick={closeInnerTemplateModal} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Layout Preview */}
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">Layout Preview</p>
                <div className="h-24 flex gap-2">
                  <div className="w-1/2 bg-white rounded shadow-sm flex flex-col items-start justify-center p-3">
                    <div className="w-3/4 h-3 bg-gray-300 rounded mb-2"></div>
                    <div className="w-full h-2 bg-gray-200 rounded mb-1"></div>
                    <div className="w-2/3 h-2 bg-gray-200 rounded mb-2"></div>
                    <div className="w-1/3 h-4 bg-theme-primary/50 rounded"></div>
                  </div>
                  <div className="w-1/2 bg-theme-primary/20 rounded flex items-center justify-center">
                    <span className="text-xs text-gray-500">Hero Image</span>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Layout Name</label>
                <input
                  type="text"
                  value={editingInnerTemplate.name}
                  onChange={(e) => setEditingInnerTemplate({ ...editingInnerTemplate, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                  placeholder="e.g., Service Page Layout"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingInnerTemplate.description || ''}
                  onChange={(e) => setEditingInnerTemplate({ ...editingInnerTemplate, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                  placeholder="Brief description of this layout"
                />
              </div>
              
              {/* Page Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Pages</label>
                <p className="text-xs text-gray-500 mb-3">Select which pages should use this layout</p>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {/* Group pages by category */}
                  {['About', 'Services', 'Dog Services', 'Cat Services', 'Patient'].map((category) => (
                    <div key={category}>
                      <div className="bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-600 sticky top-0">
                        {category}
                      </div>
                      {availablePublicPages
                        .filter(page => page.category === category)
                        .map((page) => (
                          <label 
                            key={page.path}
                            className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={editingInnerTemplate.applied_pages?.includes(page.path) || false}
                              onChange={() => togglePageSelection(page.path)}
                              className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                            />
                            <span className="text-sm text-gray-700">{page.label}</span>
                            <span className="text-xs text-gray-400 ml-auto">{page.path}</span>
                          </label>
                        ))}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {editingInnerTemplate.applied_pages?.length || 0} page(s) selected
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeInnerTemplateModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveInnerTemplate}
                disabled={saving || !editingInnerTemplate.name}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Layout
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Raw Content Generator Modal */}
      {showRawContentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
            {/* Sticky Header - Website Themed */}
            <div className="px-5 py-4 bg-blue-600 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-white" />
                <h3 className="text-lg font-semibold text-white">Generate Content from Text</h3>
              </div>
              <button
                onClick={() => {
                  setShowRawContentModal(false);
                  setRawContent('');
                }}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-5">
              <p className="text-sm text-gray-600 mb-4">
                Paste raw content from any source (webpage, document, notes) and our AI will transform it into beautifully structured page content.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raw Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rawContent}
                  onChange={(e) => setRawContent(e.target.value)}
                  placeholder="Paste your content here...

You can include:
• Text from websites
• Notes and outlines
• Product descriptions
• Service information
• Any rough content that needs formatting"
                  className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  disabled={generatingFromRaw}
                />
                <p className="mt-2 text-xs text-gray-500">
                  {rawContent.length} characters • Minimum 50 characters recommended for best results
                </p>
              </div>
              
              {/* Web Research Option */}
              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rawContentWebResearch}
                    onChange={(e) => setRawContentWebResearch(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    disabled={generatingFromRaw}
                  />
                  <Globe className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Enhance content with web research</span>
                </label>
                <p className="text-xs text-gray-400 mt-1 ml-6">
                  AI will search the web for additional context to enrich your content
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  How it works
                </h4>
                <ol className="text-sm text-blue-800 space-y-1.5 list-decimal list-inside">
                  <li>OpenAI analyzes and enhances your content</li>
                  {rawContentWebResearch && <li>Web research gathers additional context</li>}
                  <li>Claude structures it into organized sections</li>
                  <li>You review and customize the final result</li>
                </ol>
              </div>
            </div>
            
            {/* Sticky Footer */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowRawContentModal(false);
                  setRawContent('');
                  setRawContentWebResearch(false);
                }}
                disabled={generatingFromRaw}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={generateContentFromRawText}
                disabled={!rawContent.trim() || rawContent.length < 20 || generatingFromRaw}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generatingFromRaw ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Content
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder Creation Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-5 py-4 bg-theme-blue">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FolderPlus className="h-5 w-5" />
                Create New Folder
              </h3>
            </div>
            <div className="p-5">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleFolderSubmit()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                  placeholder="e.g., Services, About Us"
                  autoFocus
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  URL: {folderParentPath}{folderName ? folderName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 'folder-name'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowFolderModal(false);
                    setFolderName('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFolderSubmit}
                  disabled={!folderName.trim()}
                  className="flex-1 px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.show && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-[60] min-w-[200px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {/* Show folder context if available */}
          {contextMenu.node && (
            <div className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-100 mb-1">
              <span className="font-medium">In:</span> {contextMenu.node.path || '/'}
            </div>
          )}
          
          <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">
            Create New
          </div>
          <button
            onClick={handleCreateHomePage}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <Home className="h-4 w-4 text-blue-500" />
            Home Page
          </button>
          <button
            onClick={handleCreateInnerPage}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <FileEdit className="h-4 w-4 text-green-500" />
            Inner Page
          </button>
          {/* Show New Folder option - always available to create nested folders */}
          <button
            onClick={handleCreateFolder}
            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <FolderPlus className="h-4 w-4 text-amber-500" />
            New Folder
          </button>
          
          {/* Actions for pages/folders */}
          {contextMenu.node && contextMenu.node.path && (
            <>
              <div className="border-t border-gray-100 my-1"></div>
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase">
                Actions
              </div>
              
              {/* Edit Page - only for pages, not folders */}
              {!contextMenu.isFolder && (
                <button
                  onClick={handleEditFromContextMenu}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Edit className="h-4 w-4 text-blue-500" />
                  Edit Page
                </button>
              )}
              
              {/* Move Page - only for custom pages, not folders */}
              {!contextMenu.isFolder && (
                <button
                  onClick={handleMoveFromContextMenu}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <MoveRight className="h-4 w-4 text-blue-500" />
                  Move to Folder
                </button>
              )}
              
              {/* Delete - for both pages and folders */}
              <button
                onClick={handleDeleteFromContextMenu}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete {contextMenu.isFolder ? 'Folder' : 'Page'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xs w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Compact Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-white" />
              <h3 className="text-sm font-semibold text-white">
                Delete {deleteTarget.type === 'folder' ? 'Folder' : 'Page'}
              </h3>
            </div>
            
            {/* Modal Body */}
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-2">
                Delete <span className="font-semibold text-gray-900">&ldquo;{deleteTarget.title}&rdquo;</span>?
              </p>
              {deleteTarget.type === 'folder' && (
                <p className="text-xs text-amber-600 mb-2">
                  ⚠️ This will delete all contents inside.
                </p>
              )}
              <p className="text-xs text-gray-400">This cannot be undone.</p>
            </div>
            
            {/* Compact Footer */}
            <div className="px-4 py-3 bg-gray-50 flex gap-2">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                disabled={deleting}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {deleting ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Page Modal */}
      {showMoveModal && moveTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Compact Header - Website Themed */}
            <div className="px-4 py-3 bg-blue-600 flex items-center gap-2">
              <MoveRight className="h-4 w-4 text-white" />
              <h3 className="text-sm font-semibold text-white">Move Page</h3>
            </div>
            
            {/* Modal Body */}
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-3">
                Move <span className="font-semibold text-gray-900">&ldquo;{moveTarget.title}&rdquo;</span> to:
              </p>
              
              {/* Folder Selection */}
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {/* Root option */}
                <label className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  selectedMoveFolder === '/' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}>
                  <input
                    type="radio"
                    name="moveFolder"
                    value="/"
                    checked={selectedMoveFolder === '/'}
                    onChange={(e) => setSelectedMoveFolder(e.target.value)}
                    className="text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                  />
                  <Folder className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm text-gray-700">Root (Custom Pages)</span>
                </label>
                
                {/* Available folders */}
                {folders.map(folder => (
                  <label key={folder.id} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    selectedMoveFolder === folder.path ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      name="moveFolder"
                      value={folder.path}
                      checked={selectedMoveFolder === folder.path}
                      onChange={(e) => setSelectedMoveFolder(e.target.value)}
                      className="text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                    />
                    <Folder className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm text-gray-700 truncate">{folder.title}</span>
                  </label>
                ))}
                
                {folders.length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-3">No folders available.</p>
                )}
              </div>
            </div>
            
            {/* Compact Footer */}
            <div className="px-4 py-3 bg-gray-50 flex gap-2">
              <button
                onClick={() => { setShowMoveModal(false); setMoveTarget(null); }}
                disabled={moving}
                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmMove}
                disabled={moving}
                className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {moving ? (
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <MoveRight className="h-3 w-3" />
                    Move
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Page Modal */}
      {showCreatePageModal && (
        <CreatePageModal
          onClose={() => setShowCreatePageModal(false)}
          onCreate={createNewPage}
          parentPath={newPageParent}
          layout={newPageLayout}
        />
      )}

      {/* Component Edit Modal */}
      {showComponentModal && editingComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header - Theme Based */}
            <div 
              className="px-6 py-4 flex items-center justify-between"
              style={{ 
                background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-hover) 100%)'
              }}
            >
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {editingComponent.id ? 'Edit Component' : 'Create New Component'}
                </h2>
                <p className="text-sm text-white/80 mt-0.5">
                  {editingComponent.type === 'cta_section' && 'CTA Section Configuration'}
                  {editingComponent.type === 'dual_button_cta' && 'Dual Button CTA Configuration'}
                  {editingComponent.type === 'card_section' && 'Card Section Configuration'}
                  {editingComponent.type === 'home_hero' && 'Home Hero Section Configuration'}
                  {editingComponent.type === 'header' && 'Header Configuration'}
                  {editingComponent.type === 'footer' && 'Footer Configuration'}
                  {editingComponent.type === 'product_listing' && 'Product / Service Listing Configuration'}
                  {editingComponent.type === 'reviews' && 'Reviews Carousel Configuration'}
                  {editingComponent.type === 'service_areas' && 'Service Areas Configuration'}
                  {editingComponent.type === 'contact_info' && 'Contact Information Configuration'}
                  {editingComponent.type === 'custom' && 'Custom Component Configuration'}
                  {!editingComponent.type && 'Component Configuration'}
                </p>
              </div>
              <button onClick={closeComponentModal} className="text-white/80 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Component Name *</label>
                    <input
                      type="text"
                      value={editingComponent.name}
                      onChange={(e) => setEditingComponent({...editingComponent, name: e.target.value})}
                      placeholder="e.g., Contact CTA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                    <input
                      type="text"
                      value={editingComponent.slug}
                      onChange={(e) => setEditingComponent({...editingComponent, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                      placeholder="e.g., contact-cta"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Component Type</label>
                  <select
                    value={editingComponent.type}
                    onChange={(e) => setEditingComponent({...editingComponent, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                  >
                    <option value="cta_section">CTA Section (Call to Action)</option>
                    <option value="dual_button_cta">Dual Button CTA (Call Now + Visit)</option>
                    <option value="card_section">Card Section (Service Cards)</option>
                    <option value="home_hero">Home Page Hero Section</option>
                    <option value="product_listing">Product / Service Listing</option>
                    <option value="reviews">Reviews Carousel</option>
                    <option value="service_areas">Service Areas / Locations</option>
                    <option value="contact_info">Contact Information</option>
                    <option value="custom">Custom HTML/Content</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={editingComponent.description}
                    onChange={(e) => setEditingComponent({...editingComponent, description: e.target.value})}
                    placeholder="Brief description of this component"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                  />
                </div>
              </div>

              {/* CTA Section Config */}
              {editingComponent.type === 'cta_section' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">CTA Settings</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingComponent.config?.title || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, title: e.target.value}})}
                      placeholder="e.g., Schedule Your Appointment Today"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={editingComponent.config?.subtitle || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, subtitle: e.target.value}})}
                      placeholder="e.g., Contact us for quality care"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                      <input
                        type="text"
                        value={editingComponent.config?.button_text || ''}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, button_text: e.target.value}})}
                        placeholder="e.g., Book Now"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                      <input
                        type="text"
                        value={editingComponent.config?.button_link || ''}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, button_link: e.target.value}})}
                        placeholder="e.g., /contact"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingComponent.config?.show_phone !== false}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_phone: e.target.checked}})}
                        className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                      />
                      <span className="text-sm text-gray-700">Show Phone Number</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Style</label>
                    <select
                      value={editingComponent.config?.background_style || 'gradient'}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, background_style: e.target.value}})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    >
                      <option value="gradient">Gradient (Theme Color)</option>
                      <option value="solid">Solid (Theme Color)</option>
                      <option value="light">Light Background</option>
                      <option value="dark">Dark Background</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Dual Button CTA Config */}
              {editingComponent.type === 'dual_button_cta' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Dual Button CTA Settings</h3>
                  <p className="text-xs text-gray-500">Create a CTA section with two buttons - matching the style from Our Mission and Core Values pages</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingComponent.config?.title || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, title: e.target.value}})}
                      placeholder="e.g., Experience Our Mission in Action"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editingComponent.config?.description || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, description: e.target.value}})}
                      placeholder="e.g., Contact us today to schedule an appointment and see how we put our mission into practice"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Primary Button (Call Now)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                        <input
                          type="text"
                          value={editingComponent.config?.primary_button_text || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, primary_button_text: e.target.value}})}
                          placeholder="e.g., Call Now"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Link (optional)</label>
                        <input
                          type="text"
                          value={editingComponent.config?.primary_button_link || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, primary_button_link: e.target.value}})}
                          placeholder="Leave empty for tel: link"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_phone_in_primary !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_phone_in_primary: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Phone Number in Button Text</span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Secondary Button (Schedule Visit)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                        <input
                          type="text"
                          value={editingComponent.config?.secondary_button_text || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, secondary_button_text: e.target.value}})}
                          placeholder="e.g., Schedule Visit"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                        <input
                          type="text"
                          value={editingComponent.config?.secondary_button_link || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, secondary_button_link: e.target.value}})}
                          placeholder="e.g., /reach-us"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Home Hero Section Config */}
              {editingComponent.type === 'home_hero' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Home Hero Section Settings</h3>
                  <p className="text-xs text-gray-500">Configure the home page hero section with customizable content and visibility options</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Layout Style</label>
                    <select
                      value={editingComponent.config?.layout || 'split'}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout: e.target.value}})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    >
                      <option value="split">Split Layout (Text Left, Image Right)</option>
                      <option value="full_width">Full Width Hero (Image Background)</option>
                    </select>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Hero Content</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_badge !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_badge: e.target.checked}})}
                            className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                          />
                          <span className="text-sm font-medium text-gray-700">Show Badge</span>
                        </label>
                        <input
                          type="text"
                          value={editingComponent.config?.badge_text || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, badge_text: e.target.value}})}
                          placeholder="e.g., Where Every Paw Finds Care"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
                        <input
                          type="text"
                          value={editingComponent.config?.main_title || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, main_title: e.target.value}})}
                          placeholder="e.g., Compassionate Care for Your"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Highlighted Title Text</label>
                        <input
                          type="text"
                          value={editingComponent.config?.main_title_highlight || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, main_title_highlight: e.target.value}})}
                          placeholder="e.g., Beloved Pets"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editingComponent.config?.description || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, description: e.target.value}})}
                          placeholder="e.g., Quality veterinary care and specialty services..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Slider Images</h4>
                    
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_slider_text !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_slider_text: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm font-medium text-gray-700">Show Slider Image Text Overlay (Title & Description)</span>
                      </label>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Slider Images to Display
                          <span className="text-xs text-gray-500 ml-2">(Leave empty to show all images)</span>
                        </label>
                        
                        {sliderImagesLoading ? (
                          <div className="text-sm text-gray-500 py-4">Loading slider images...</div>
                        ) : availableSliderImages.length > 0 ? (
                          <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {availableSliderImages.map((image) => (
                              <label key={image.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(editingComponent.config?.selected_slider_images || []).includes(image.id)}
                                  onChange={(e) => {
                                    const currentSelected = editingComponent.config?.selected_slider_images || [];
                                    const newSelected = e.target.checked
                                      ? [...currentSelected, image.id]
                                      : currentSelected.filter(id => id !== image.id);
                                    setEditingComponent({
                                      ...editingComponent,
                                      config: {
                                        ...editingComponent.config,
                                        selected_slider_images: newSelected
                                      }
                                    });
                                  }}
                                  className="w-4 h-4 mt-1 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    {image.image_url && (
                                      <img 
                                        src={image.image_url} 
                                        alt={image.title || 'Slider image'} 
                                        className="w-16 h-10 object-cover rounded"
                                      />
                                    )}
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900">{image.title || 'Untitled Image'}</p>
                                      {image.description && (
                                        <p className="text-xs text-gray-500 line-clamp-1">{image.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 py-4 text-center border border-gray-200 rounded-lg">
                            No slider images available. Add images in Settings → Slider Images.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Visibility Options</h4>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_urgent_care_hours !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_urgent_care_hours: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Urgent Care Hours</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_general_hours !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_general_hours: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show General Practice Hours</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_call_button !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_call_button: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Call Button</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_checkin_button !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_checkin_button: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Check In Online Button</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_slider_dots !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_slider_dots: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Image Slider Dots</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_slider_arrows !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_slider_arrows: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Image Slider Arrows</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Card Section Config */}
              {editingComponent.type === 'card_section' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Card Section Settings</h3>
                  <p className="text-xs text-gray-500">Create a card section like Specialty Care or General Practice sections</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingComponent.config?.title || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, title: e.target.value}})}
                      placeholder="e.g., Our Services"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Subtitle</label>
                    <textarea
                      value={editingComponent.config?.subtitle || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, subtitle: e.target.value}})}
                      placeholder="e.g., Comprehensive care for your beloved pets"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Style</label>
                    <select
                      value={editingComponent.config?.background_style || 'white'}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, background_style: e.target.value}})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    >
                      <option value="white">White Background (Blue Button)</option>
                      <option value="theme">Theme Color Background (White Button)</option>
                    </select>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase">Cards</h4>
                      <button
                        type="button"
                        onClick={() => {
                          const cards = editingComponent.config?.cards || [];
                          setEditingComponent({
                            ...editingComponent,
                            config: {
                              ...editingComponent.config,
                              cards: [
                                ...cards,
                                {
                                  title: 'Card Title',
                                  description: 'Card description',
                                  icon: 'check-circle',
                                  icon_color: 'var(--theme-primary)',
                                  icon_bg: 'var(--theme-primary-light)',
                                  link: '',
                                  link_text: ''
                                }
                              ]
                            }
                          });
                        }}
                        className="px-3 py-1.5 bg-theme-blue text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        + Add Card
                      </button>
                    </div>

                    {(editingComponent.config?.cards || []).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4 border border-gray-200 rounded-lg">
                        No cards added yet. Click &quot;Add Card&quot; to create cards.
                      </p>
                    )}

                    <div className="space-y-3">
                      {(editingComponent.config?.cards || []).map((card, cardIndex) => (
                        <div key={cardIndex} className="p-4 border border-gray-200 rounded-lg space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-medium text-gray-700">Card {cardIndex + 1}</h5>
                            <button
                              type="button"
                              onClick={() => {
                                const cards = [...(editingComponent.config?.cards || [])];
                                cards.splice(cardIndex, 1);
                                setEditingComponent({
                                  ...editingComponent,
                                  config: {
                                    ...editingComponent.config,
                                    cards
                                  }
                                });
                              }}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                            <select
                              value={card.icon || 'check-circle'}
                              onChange={(e) => {
                                const cards = [...(editingComponent.config?.cards || [])];
                                cards[cardIndex].icon = e.target.value;
                                setEditingComponent({
                                  ...editingComponent,
                                  config: {
                                    ...editingComponent.config,
                                    cards
                                  }
                                });
                              }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                            >
                              <option value="check-circle">Check Circle</option>
                              <option value="heart">Heart</option>
                              <option value="shield">Shield</option>
                              <option value="star">Star</option>
                              <option value="award">Award</option>
                              <option value="users">Users</option>
                              <option value="clock">Clock</option>
                              <option value="map-pin">Map Pin</option>
                              <option value="phone">Phone</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                            <input
                              type="text"
                              value={card.title || ''}
                              onChange={(e) => {
                                const cards = [...(editingComponent.config?.cards || [])];
                                cards[cardIndex].title = e.target.value;
                                setEditingComponent({
                                  ...editingComponent,
                                  config: {
                                    ...editingComponent.config,
                                    cards
                                  }
                                });
                              }}
                              placeholder="Card title"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={card.description || ''}
                              onChange={(e) => {
                                const cards = [...(editingComponent.config?.cards || [])];
                                cards[cardIndex].description = e.target.value;
                                setEditingComponent({
                                  ...editingComponent,
                                  config: {
                                    ...editingComponent.config,
                                    cards
                                  }
                                });
                              }}
                              placeholder="Card description"
                              rows={2}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Link (optional)</label>
                              <input
                                type="text"
                                value={card.link || ''}
                                onChange={(e) => {
                                  const cards = [...(editingComponent.config?.cards || [])];
                                  cards[cardIndex].link = e.target.value;
                                  setEditingComponent({
                                    ...editingComponent,
                                    config: {
                                      ...editingComponent.config,
                                      cards
                                    }
                                  });
                                }}
                                placeholder="/page-url"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Link Text</label>
                              <input
                                type="text"
                                value={card.link_text || ''}
                                onChange={(e) => {
                                  const cards = [...(editingComponent.config?.cards || [])];
                                  cards[cardIndex].link_text = e.target.value;
                                  setEditingComponent({
                                    ...editingComponent,
                                    config: {
                                      ...editingComponent.config,
                                      cards
                                    }
                                  });
                                }}
                                placeholder="Learn More"
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Button Settings</h4>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_button !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_button: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Button</span>
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                        <input
                          type="text"
                          value={editingComponent.config?.button_text || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, button_text: e.target.value}})}
                          placeholder="e.g., View All Services"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                        <input
                          type="text"
                          value={editingComponent.config?.button_link || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, button_link: e.target.value}})}
                          placeholder="e.g., /services"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Header Config */}
              {editingComponent.type === 'header' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Header Configuration</h3>
                  <p className="text-xs text-gray-500">Configure your website header layout, menus, and theme colors</p>
                  
                  {/* Layout Selection */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Layout Style</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Logo Left Layout */}
                      <div 
                        onClick={() => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout: 'logo-left'}})}
                        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                          editingComponent.config?.layout === 'logo-left' || !editingComponent.config?.layout 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center mb-2">
                          <div className="flex items-center justify-between bg-gray-100 rounded p-2 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-purple-500 rounded"></div>
                              <span className="font-medium">Logo</span>
                            </div>
                            <div className="flex gap-1 text-gray-500">
                              <span>Menu</span>
                              <span>Menu</span>
                              <span>Menu</span>
                            </div>
                            <div className="bg-purple-500 text-white px-2 py-0.5 rounded text-[10px]">CTA</div>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-gray-700 text-center">Logo Left</p>
                        <p className="text-xs text-gray-500 text-center">Logo and business name on the left</p>
                      </div>

                      {/* Logo Center Layout */}
                      <div 
                        onClick={() => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout: 'logo-center'}})}
                        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                          editingComponent.config?.layout === 'logo-center' 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-center mb-2">
                          <div className="flex items-center justify-between bg-gray-100 rounded p-2 text-xs">
                            <div className="flex gap-1 text-gray-500">
                              <span>Menu</span>
                              <span>Menu</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-4 bg-purple-500 rounded"></div>
                              <span className="font-medium">Logo</span>
                            </div>
                            <div className="flex gap-1 text-gray-500">
                              <span>Menu</span>
                              <span>Menu</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-gray-700 text-center">Logo Center</p>
                        <p className="text-xs text-gray-500 text-center">Logo centered with menus on both sides</p>
                      </div>
                    </div>
                  </div>

                  {/* Top Bar Settings */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Top Bar Settings</h4>
                    
                    <label className="flex items-center gap-2 cursor-pointer mb-3">
                      <input
                        type="checkbox"
                        checked={editingComponent.config?.show_top_bar !== false}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_top_bar: e.target.checked}})}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Show Top Bar</span>
                    </label>

                    {editingComponent.config?.show_top_bar !== false && (
                      <div className="ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_phone !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_phone: e.target.checked}})}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-600">Show Phone Number</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_address !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_address: e.target.checked}})}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-600">Show Address</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_hours_status !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_hours_status: e.target.checked}})}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-600">Show Hours Status (Open/Closed)</span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Logo & Business Name Settings */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Logo & Business Name</h4>
                    
                    <div className="space-y-4">
                      {/* Display Mode */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Display Mode</label>
                        <select
                          value={editingComponent.config?.logo_display_mode || 'logo_name_tagline'}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, logo_display_mode: e.target.value}})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="logo_only">Logo Only</option>
                          <option value="logo_name">Logo + Business Name</option>
                          <option value="logo_name_tagline">Logo + Business Name + Tagline</option>
                          <option value="name_only">Business Name Only (No Logo)</option>
                          <option value="name_tagline">Business Name + Tagline (No Logo)</option>
                        </select>
                      </div>

                      {/* Text Alignment */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">Text Alignment</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingComponent({...editingComponent, config: {...editingComponent.config, logo_text_align: 'left'}})}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg transition-colors ${
                              (!editingComponent.config?.logo_text_align || editingComponent.config?.logo_text_align === 'left')
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            Left Aligned
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingComponent({...editingComponent, config: {...editingComponent.config, logo_text_align: 'center'}})}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg transition-colors ${
                              editingComponent.config?.logo_text_align === 'center'
                                ? 'border-purple-500 bg-purple-50 text-purple-700'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            Center Aligned
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Applies to business name and tagline text</p>
                      </div>

                      {/* Preview */}
                      <div className="p-3 bg-gray-100 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">Preview:</p>
                        <div className={`flex items-center gap-2 ${editingComponent.config?.logo_text_align === 'center' ? 'justify-center' : ''}`}>
                          {(editingComponent.config?.logo_display_mode || 'logo_name_tagline') !== 'name_only' && 
                           (editingComponent.config?.logo_display_mode || 'logo_name_tagline') !== 'name_tagline' && (
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: editingComponent.config?.theme_color || '#0891b2' }}>
                              <div className="w-6 h-6 bg-white rounded flex items-center justify-center text-xs font-bold" style={{ color: editingComponent.config?.theme_color || '#0891b2' }}>+</div>
                            </div>
                          )}
                          <div className={editingComponent.config?.logo_text_align === 'center' ? 'text-center' : ''}>
                            {(editingComponent.config?.logo_display_mode || 'logo_name_tagline') !== 'logo_only' && (
                              <p className="text-sm font-bold text-gray-900">Business Name</p>
                            )}
                            {((editingComponent.config?.logo_display_mode || 'logo_name_tagline') === 'logo_name_tagline' || 
                              (editingComponent.config?.logo_display_mode || 'logo_name_tagline') === 'name_tagline') && (
                              <p className="text-xs text-gray-500">Tagline goes here</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Theme Colors */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Theme Colors</h4>
                    <p className="text-xs text-gray-500 mb-3">Select colors for header elements. These will override the global theme for the header.</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Primary / Top Bar Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editingComponent.config?.theme_color || '#0891b2'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, theme_color: e.target.value, top_bar_bg_color: e.target.value}})}
                            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                          />
                          <input
                            type="text"
                            value={editingComponent.config?.theme_color || '#0891b2'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, theme_color: e.target.value, top_bar_bg_color: e.target.value}})}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="#0891b2"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Menu Text Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editingComponent.config?.menu_text_color || '#374151'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, menu_text_color: e.target.value}})}
                            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                          />
                          <input
                            type="text"
                            value={editingComponent.config?.menu_text_color || '#374151'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, menu_text_color: e.target.value}})}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="#374151"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Menu Hover Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editingComponent.config?.menu_hover_color || '#0891b2'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, menu_hover_color: e.target.value}})}
                            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                          />
                          <input
                            type="text"
                            value={editingComponent.config?.menu_hover_color || '#0891b2'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, menu_hover_color: e.target.value}})}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="#0891b2"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Top Bar Text Color</label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editingComponent.config?.top_bar_text_color || '#ffffff'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, top_bar_text_color: e.target.value}})}
                            className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                          />
                          <input
                            type="text"
                            value={editingComponent.config?.top_bar_text_color || '#ffffff'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, top_bar_text_color: e.target.value}})}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Color Presets */}
                    <div className="mt-4">
                      <label className="block text-xs font-medium text-gray-600 mb-2">Quick Color Presets</label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { name: 'Teal', primary: '#0891b2', hover: '#0e7490' },
                          { name: 'Blue', primary: '#2563eb', hover: '#1d4ed8' },
                          { name: 'Purple', primary: '#7c3aed', hover: '#6d28d9' },
                          { name: 'Green', primary: '#059669', hover: '#047857' },
                          { name: 'Red', primary: '#dc2626', hover: '#b91c1c' },
                          { name: 'Orange', primary: '#ea580c', hover: '#c2410c' },
                          { name: 'Pink', primary: '#db2777', hover: '#be185d' },
                          { name: 'Slate', primary: '#475569', hover: '#334155' }
                        ].map((preset) => (
                          <button
                            key={preset.name}
                            type="button"
                            onClick={() => setEditingComponent({
                              ...editingComponent, 
                              config: {
                                ...editingComponent.config, 
                                theme_color: preset.primary,
                                top_bar_bg_color: preset.primary,
                                menu_hover_color: preset.primary
                              }
                            })}
                            className="flex items-center gap-1.5 px-2 py-1 border rounded hover:bg-gray-50 transition-colors"
                            style={{ borderColor: preset.primary }}
                          >
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.primary }}></div>
                            <span className="text-xs">{preset.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Navigation Menus</h4>
                    <p className="text-xs text-gray-500 mb-3">Enable/disable top-level menus, set position, and choose icons.</p>
                    
                    {/* Global Menu Display Mode */}
                    <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-purple-700 mb-2">Menu Display Mode</label>
                          <select
                            value={editingComponent.config?.menu_display_mode || 'text_only'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, menu_display_mode: e.target.value}})}
                            className="w-full px-2 py-1.5 text-sm border border-purple-300 rounded focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="text_only">Text Only</option>
                            <option value="icon_only">Icon Only</option>
                            <option value="icon_text">Icon + Text</option>
                            <option value="text_icon">Text + Icon</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-purple-700 mb-2">Icon Size</label>
                          <select
                            value={editingComponent.config?.menu_icon_size || 'regular'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, menu_icon_size: e.target.value}})}
                            className="w-full px-2 py-1.5 text-sm border border-purple-300 rounded focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="small">Small (14px)</option>
                            <option value="regular">Regular (16px)</option>
                            <option value="medium">Medium (20px)</option>
                            <option value="large">Large (24px)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {(editingComponent.config?.top_menus || [
                        { id: 'menu_home', label: 'Home', url: '/', enabled: true, position: 'left', icon: 'Home' },
                        { id: 'menu_about', label: 'About', url: '/about', enabled: true, position: 'left', has_submenu: true, icon: 'Info' },
                        { id: 'menu_services', label: 'Services', url: '/services', enabled: true, position: 'left', has_submenu: true, icon: 'Briefcase' },
                        { id: 'menu_patient', label: 'Patient', url: '/patient', enabled: true, position: 'right', has_submenu: true, icon: 'User' },
                        { id: 'menu_contact', label: 'Contact', url: '/contact', enabled: true, position: 'right', has_submenu: true, icon: 'Phone' }
                      ]).map((menu, index) => (
                        <div key={menu.id || index} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <input
                              type="checkbox"
                              checked={menu.enabled !== false}
                              onChange={(e) => {
                                const menus = [...(editingComponent.config?.top_menus || [])];
                                menus[index] = { ...menu, enabled: e.target.checked };
                                setEditingComponent({...editingComponent, config: {...editingComponent.config, top_menus: menus}});
                              }}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <input
                                type="text"
                                value={menu.label}
                                onChange={(e) => {
                                  const menus = [...(editingComponent.config?.top_menus || [])];
                                  menus[index] = { ...menu, label: e.target.value };
                                  setEditingComponent({...editingComponent, config: {...editingComponent.config, top_menus: menus}});
                                }}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                placeholder="Menu Label"
                              />
                            </div>
                            <select
                              value={menu.position || 'left'}
                              onChange={(e) => {
                                const menus = [...(editingComponent.config?.top_menus || [])];
                                menus[index] = { ...menu, position: e.target.value };
                                setEditingComponent({...editingComponent, config: {...editingComponent.config, top_menus: menus}});
                              }}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                              <option value="left">Left Side</option>
                              <option value="right">Right Side</option>
                            </select>
                            {menu.has_submenu && (
                              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">Has Submenu</span>
                            )}
                          </div>
                          
                          {/* Icon Selection Row */}
                          <div className="flex items-center gap-2 ml-7">
                            <label className="text-xs text-gray-500">Icon:</label>
                            <select
                              value={menu.icon || 'none'}
                              onChange={(e) => {
                                const menus = [...(editingComponent.config?.top_menus || [])];
                                menus[index] = { ...menu, icon: e.target.value };
                                setEditingComponent({...editingComponent, config: {...editingComponent.config, top_menus: menus}});
                              }}
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                            >
                              <option value="none">No Icon</option>
                              <optgroup label="Navigation">
                                <option value="Home">Home</option>
                                <option value="Menu">Menu</option>
                                <option value="Search">Search</option>
                                <option value="ArrowRight">Arrow Right</option>
                              </optgroup>
                              <optgroup label="Information">
                                <option value="Info">Info</option>
                                <option value="HelpCircle">Help Circle</option>
                                <option value="BookOpen">Book Open</option>
                                <option value="FileText">File / Document</option>
                              </optgroup>
                              <optgroup label="Business">
                                <option value="Briefcase">Briefcase</option>
                                <option value="Building">Building</option>
                                <option value="Store">Store</option>
                                <option value="ShoppingCart">Shopping Cart</option>
                                <option value="CreditCard">Credit Card</option>
                              </optgroup>
                              <optgroup label="People">
                                <option value="User">User</option>
                                <option value="Users">Users / Team</option>
                                <option value="UserCircle">User Circle</option>
                                <option value="Heart">Heart</option>
                              </optgroup>
                              <optgroup label="Contact">
                                <option value="Phone">Phone</option>
                                <option value="Mail">Mail / Email</option>
                                <option value="MapPin">Map Pin / Location</option>
                                <option value="MessageCircle">Message</option>
                                <option value="Send">Send</option>
                              </optgroup>
                              <optgroup label="Medical">
                                <option value="Stethoscope">Stethoscope</option>
                                <option value="Activity">Activity / Pulse</option>
                                <option value="Thermometer">Thermometer</option>
                                <option value="Pill">Pill</option>
                                <option value="Syringe">Syringe</option>
                                <option value="HeartPulse">Heart Pulse</option>
                              </optgroup>
                              <optgroup label="Animals">
                                <option value="PawPrint">Paw Print</option>
                                <option value="Dog">Dog</option>
                                <option value="Cat">Cat</option>
                                <option value="Bird">Bird</option>
                              </optgroup>
                              <optgroup label="Actions">
                                <option value="Calendar">Calendar</option>
                                <option value="Clock">Clock</option>
                                <option value="Star">Star</option>
                                <option value="Settings">Settings / Gear</option>
                                <option value="Bell">Bell / Notification</option>
                                <option value="Gift">Gift</option>
                              </optgroup>
                              <optgroup label="Social">
                                <option value="Share">Share</option>
                                <option value="ThumbsUp">Thumbs Up</option>
                                <option value="Award">Award / Trophy</option>
                                <option value="Globe">Globe / Web</option>
                              </optgroup>
                            </select>
                            {menu.icon && menu.icon !== 'none' && (
                              <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                                {menu.icon}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Add Menu Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const menus = [...(editingComponent.config?.top_menus || [])];
                          menus.push({
                            id: `menu_${Date.now()}`,
                            label: 'New Menu',
                            url: '/',
                            enabled: true,
                            position: 'left',
                            icon: 'none'
                          });
                          setEditingComponent({...editingComponent, config: {...editingComponent.config, top_menus: menus}});
                        }}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors"
                      >
                        + Add Menu Item
                      </button>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">CTA Buttons</h4>
                    <p className="text-xs text-gray-500 mb-3">Add prominent call-to-action buttons (e.g., &quot;Book Now&quot;, &quot;Specialty Care&quot;)</p>
                    
                    <div className="space-y-3">
                      {(editingComponent.config?.cta_buttons || []).map((btn, index) => (
                        <div key={btn.id || index} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={btn.enabled !== false}
                              onChange={(e) => {
                                const buttons = [...(editingComponent.config?.cta_buttons || [])];
                                buttons[index] = { ...btn, enabled: e.target.checked };
                                setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                              }}
                              className="w-4 h-4 mt-2 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1 space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={btn.label}
                                  onChange={(e) => {
                                    const buttons = [...(editingComponent.config?.cta_buttons || [])];
                                    buttons[index] = { ...btn, label: e.target.value };
                                    setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                                  }}
                                  className="px-2 py-1.5 text-sm border border-gray-300 rounded"
                                  placeholder="Button Label"
                                />
                                <input
                                  type="text"
                                  value={btn.url}
                                  onChange={(e) => {
                                    const buttons = [...(editingComponent.config?.cta_buttons || [])];
                                    buttons[index] = { ...btn, url: e.target.value };
                                    setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                                  }}
                                  className="px-2 py-1.5 text-sm border border-gray-300 rounded"
                                  placeholder="URL (e.g., /book)"
                                />
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-gray-600">BG Color:</label>
                                  <input
                                    type="color"
                                    value={btn.bg_color || '#0891b2'}
                                    onChange={(e) => {
                                      const buttons = [...(editingComponent.config?.cta_buttons || [])];
                                      buttons[index] = { ...btn, bg_color: e.target.value };
                                      setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                                    }}
                                    className="w-8 h-8 rounded cursor-pointer border border-gray-300"
                                  />
                                </div>
                                <select
                                  value={btn.position || 'right'}
                                  onChange={(e) => {
                                    const buttons = [...(editingComponent.config?.cta_buttons || [])];
                                    buttons[index] = { ...btn, position: e.target.value };
                                    setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                                  }}
                                  className="px-2 py-1 text-sm border border-gray-300 rounded"
                                >
                                  <option value="left">Left Side</option>
                                  <option value="right">Right Side</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const buttons = editingComponent.config?.cta_buttons?.filter((_, i) => i !== index) || [];
                                    setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                                  }}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          {/* Preview */}
                          <div className="mt-2 pt-2 border-t border-purple-200">
                            <span className="text-xs text-gray-500">Preview:</span>
                            <button
                              type="button"
                              className="ml-2 px-3 py-1 text-white text-xs rounded-lg font-medium"
                              style={{ backgroundColor: btn.bg_color || '#0891b2' }}
                            >
                              {btn.label || 'Button'}
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add CTA Button */}
                      <button
                        type="button"
                        onClick={() => {
                          const buttons = [...(editingComponent.config?.cta_buttons || [])];
                          buttons.push({
                            id: `btn_${Date.now()}`,
                            label: 'New Button',
                            url: '/',
                            enabled: true,
                            bg_color: editingComponent.config?.theme_color || '#0891b2',
                            position: 'right'
                          });
                          setEditingComponent({...editingComponent, config: {...editingComponent.config, cta_buttons: buttons}});
                        }}
                        className="w-full py-2 border-2 border-dashed border-purple-300 rounded-lg text-sm text-purple-500 hover:border-purple-400 hover:text-purple-600 transition-colors"
                      >
                        + Add CTA Button
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Config */}
              {editingComponent.type === 'footer' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Footer Configuration</h3>
                  <p className="text-xs text-gray-500">Configure what appears in your website footer (global across all pages)</p>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Visibility Settings</h4>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_contact_info !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_contact_info: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Contact Information Section</span>
                      </label>

                      <div className="ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_address !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_address: e.target.checked}})}
                            className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                          />
                          <span className="text-sm text-gray-600">Show Address</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_phone !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_phone: e.target.checked}})}
                            className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                          />
                          <span className="text-sm text-gray-600">Show Phone Number</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_email !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_email: e.target.checked}})}
                            className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                          />
                          <span className="text-sm text-gray-600">Show Email</span>
                        </label>
                      </div>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_hours !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_hours: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Business Hours</span>
                      </label>

                      {/* Sub-options for business hours */}
                      {editingComponent.config?.show_hours !== false && (
                        <div className="ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingComponent.config?.show_first_business_hours !== false}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_first_business_hours: e.target.checked}})}
                              className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                            />
                            <span className="text-sm text-gray-600">Show First Business Hours (from Configure Hours)</span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingComponent.config?.show_second_business_hours !== false}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_second_business_hours: e.target.checked}})}
                              className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                            />
                            <span className="text-sm text-gray-600">Show Second Business Hours (if enabled in Configure Hours)</span>
                          </label>
                        </div>
                      )}

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_social_media !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_social_media: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Social Media Links</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_quick_links !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_quick_links: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Quick Links</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_legal_links !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_legal_links: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Legal Links (Terms, Privacy, Accessibility)</span>
                      </label>
                    </div>
                  </div>

                  {/* Social Media Links */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase">Social Media Links</h4>
                    </div>

                    {/* Toggle for using business info social media */}
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.use_business_info_social !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, use_business_info_social: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm font-medium text-blue-900">Use Social Media from Business Information</span>
                      </label>
                      <p className="text-xs text-blue-700 mt-1 ml-6">When enabled, social media links are pulled from Business Information page</p>
                    </div>

                    {editingComponent.config?.use_business_info_social !== false ? (
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-sm text-gray-600">Social media links will be automatically loaded from Business Information settings</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-gray-500">Add custom social media links</span>
                          <button
                            type="button"
                            onClick={() => {
                              const socialMedia = editingComponent.config?.social_media || [];
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  social_media: [
                                    ...socialMedia,
                                    { platform: 'facebook', url: '' }
                                  ]
                                }
                              });
                            }}
                            className="px-3 py-1.5 bg-theme-blue text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            + Add Social Link
                          </button>
                        </div>

                        <div className="space-y-2">
                          {(editingComponent.config?.social_media || []).map((social, index) => (
                        <div key={index} className="flex gap-2">
                          <select
                            value={social.platform}
                            onChange={(e) => {
                              const socialMedia = [...(editingComponent.config?.social_media || [])];
                              socialMedia[index].platform = e.target.value;
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  social_media: socialMedia
                                }
                              });
                            }}
                            className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          >
                            <option value="facebook">Facebook</option>
                            <option value="instagram">Instagram</option>
                            <option value="twitter">Twitter/X</option>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="linkedin">LinkedIn</option>
                            <option value="youtube">YouTube</option>
                          </select>
                          <input
                            type="url"
                            value={social.url}
                            onChange={(e) => {
                              const socialMedia = [...(editingComponent.config?.social_media || [])];
                              socialMedia[index].url = e.target.value;
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  social_media: socialMedia
                                }
                              });
                            }}
                            placeholder="https://..."
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const socialMedia = [...(editingComponent.config?.social_media || [])];
                              socialMedia.splice(index, 1);
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  social_media: socialMedia
                                }
                              });
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                      </>
                    )}
                  </div>

                  {/* Quick Links */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase">Quick Links</h4>
                      <button
                        type="button"
                        onClick={() => {
                          const quickLinks = editingComponent.config?.quick_links || [];
                          setEditingComponent({
                            ...editingComponent,
                            config: {
                              ...editingComponent.config,
                              quick_links: [
                                ...quickLinks,
                                { label: '', url: '' }
                              ]
                            }
                          });
                        }}
                        className="px-3 py-1.5 bg-theme-blue text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        + Add Link
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(editingComponent.config?.quick_links || []).map((link, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={link.label}
                            onChange={(e) => {
                              const quickLinks = [...(editingComponent.config?.quick_links || [])];
                              quickLinks[index].label = e.target.value;
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  quick_links: quickLinks
                                }
                              });
                            }}
                            placeholder="Link Label"
                            className="w-32 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          />
                          <input
                            type="text"
                            value={link.url}
                            onChange={(e) => {
                              const quickLinks = [...(editingComponent.config?.quick_links || [])];
                              quickLinks[index].url = e.target.value;
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  quick_links: quickLinks
                                }
                              });
                            }}
                            placeholder="/page-url"
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const quickLinks = [...(editingComponent.config?.quick_links || [])];
                              quickLinks.splice(index, 1);
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  quick_links: quickLinks
                                }
                              });
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Legal Links */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase">Legal Links</h4>
                      <button
                        type="button"
                        onClick={() => {
                          const legalLinks = editingComponent.config?.legal_links || [];
                          setEditingComponent({
                            ...editingComponent,
                            config: {
                              ...editingComponent.config,
                              legal_links: [
                                ...legalLinks,
                                { label: '', url: '' }
                              ]
                            }
                          });
                        }}
                        className="px-3 py-1.5 bg-theme-blue text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        + Add Link
                      </button>
                    </div>

                    <div className="space-y-2">
                      {(editingComponent.config?.legal_links || []).map((link, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={link.label}
                            onChange={(e) => {
                              const legalLinks = [...(editingComponent.config?.legal_links || [])];
                              legalLinks[index].label = e.target.value;
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  legal_links: legalLinks
                                }
                              });
                            }}
                            placeholder="e.g., Privacy Policy"
                            className="w-40 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          />
                          <input
                            type="text"
                            value={link.url}
                            onChange={(e) => {
                              const legalLinks = [...(editingComponent.config?.legal_links || [])];
                              legalLinks[index].url = e.target.value;
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  legal_links: legalLinks
                                }
                              });
                            }}
                            placeholder="/privacy"
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const legalLinks = [...(editingComponent.config?.legal_links || [])];
                              legalLinks.splice(index, 1);
                              setEditingComponent({
                                ...editingComponent,
                                config: {
                                  ...editingComponent.config,
                                  legal_links: legalLinks
                                }
                              });
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Copyright Text */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
                    <input
                      type="text"
                      value={editingComponent.config?.copyright_text || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, copyright_text: e.target.value}})}
                      placeholder={`© ${new Date().getFullYear()} All Rights Reserved`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>

                  {/* Section Labels */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Section Labels</h4>
                    <p className="text-xs text-gray-500 mb-4">Customize the header labels for each footer section</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Quick Links Label</label>
                        <input
                          type="text"
                          value={editingComponent.config?.label_quick_links || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, label_quick_links: e.target.value}})}
                          placeholder="Quick Links"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Follow Us Label</label>
                        <input
                          type="text"
                          value={editingComponent.config?.label_follow_us || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, label_follow_us: e.target.value}})}
                          placeholder="Follow Us"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Additional Locations Label</label>
                        <input
                          type="text"
                          value={editingComponent.config?.label_locations || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, label_locations: e.target.value}})}
                          placeholder="Our Locations"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">First Business Hours Label</label>
                        <input
                          type="text"
                          value={editingComponent.config?.label_first_hours || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, label_first_hours: e.target.value}})}
                          placeholder="Store Hours"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Second Business Hours Label</label>
                        <input
                          type="text"
                          value={editingComponent.config?.label_second_hours || ''}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, label_second_hours: e.target.value}})}
                          placeholder="Urgent Care Hours"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Layout Configuration */}
                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-3">Layout Configuration</h4>
                    <p className="text-xs text-gray-500 mb-4">Configure which column each section appears in (Left, Center-Left, Center-Right, Right)</p>
                    
                    {/* Primary Address & Business Name */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Address & Business Name</label>
                      <select
                        value={editingComponent.config?.layout_primary_address || 'left'}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout_primary_address: e.target.value}})}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                      >
                        <option value="left">Left Column</option>
                        <option value="center-left">Center-Left Column</option>
                        <option value="center-right">Center-Right Column</option>
                        <option value="right">Right Column</option>
                      </select>
                    </div>

                    {/* Additional Locations */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Additional Locations</label>
                      <p className="text-xs text-gray-500 mb-2">Locations are configured in Business Information → Locations tab</p>
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_additional_locations !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_additional_locations: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Additional Locations in Footer</span>
                      </label>
                      {editingComponent.config?.show_additional_locations !== false && (
                        <select
                          value={editingComponent.config?.layout_additional_locations || 'below-primary'}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout_additional_locations: e.target.value}})}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                        >
                          <option value="below-primary">Below Primary Address</option>
                          <option value="left">Left Column (Separate)</option>
                          <option value="center-left">Center-Left Column</option>
                          <option value="center-right">Center-Right Column</option>
                          <option value="right">Right Column</option>
                        </select>
                      )}
                    </div>

                    {/* Quick Links Layout */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quick Links</label>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Display Style</label>
                          <select
                            value={editingComponent.config?.quick_links_columns || '1'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, quick_links_columns: e.target.value}})}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          >
                            <option value="1">Single Column</option>
                            <option value="2">Two Columns (Grid)</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Position</label>
                          <select
                            value={editingComponent.config?.layout_quick_links || 'right'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout_quick_links: e.target.value}})}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          >
                            <option value="left">Left Column</option>
                            <option value="center-left">Center-Left Column</option>
                            <option value="center-right">Center-Right Column</option>
                            <option value="right">Right Column</option>
                          </select>
                        </div>
                        {editingComponent.config?.quick_links_columns === '2' && (
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Span Additional Column</label>
                            <select
                              value={editingComponent.config?.layout_quick_links_span || 'none'}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout_quick_links_span: e.target.value}})}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                            >
                              <option value="none">No (Stay in Single Column)</option>
                              <option value="span-2">Yes (Span 2 Footer Columns)</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Follow Us / Social Media Layout */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Follow Us / Social Media</label>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Display Style</label>
                          <select
                            value={editingComponent.config?.social_media_style || 'icons'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, social_media_style: e.target.value}})}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          >
                            <option value="icons">Icons Only (Horizontal)</option>
                            <option value="list">List with Labels (Vertical)</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Position</label>
                          <select
                            value={editingComponent.config?.layout_social_media || 'below-primary'}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout_social_media: e.target.value}})}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                          >
                            <option value="below-primary">Below Primary Address</option>
                            <option value="left">Left Column (Separate)</option>
                            <option value="center-left">Center-Left Column</option>
                            <option value="center-right">Center-Right Column</option>
                            <option value="right">Right Column</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Business Hours Layout */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours</label>
                      <select
                        value={editingComponent.config?.layout_business_hours || 'center-left'}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, layout_business_hours: e.target.value}})}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-theme-blue"
                      >
                        <option value="left">Left Column</option>
                        <option value="center-left">Center-Left Column</option>
                        <option value="center-right">Center-Right Column</option>
                        <option value="right">Right Column</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Service Areas Config */}
              {editingComponent.type === 'service_areas' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Service Areas Settings</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingComponent.config?.title || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, title: e.target.value}})}
                      placeholder="e.g., Serving Your Community"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Areas (comma separated)</label>
                    <textarea
                      value={editingComponent.config?.areas?.join(', ') || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, areas: e.target.value.split(',').map(a => a.trim()).filter(a => a)}})}
                      placeholder="e.g., Chantilly, South Riding, Aldie, Ashburn"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Reviews Config */}
              {editingComponent.type === 'reviews' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Reviews Display Settings</h3>
                  
                  {/* Info about where sync is managed */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-800">
                      <strong>📍 Note:</strong> Google Place ID, API Key, and sync settings are managed in:
                    </p>
                    <ul className="text-xs text-blue-700 mt-2 space-y-1 list-disc list-inside">
                      <li><strong>Settings → CMS → Google tab</strong> - Configure Place ID & API Key</li>
                      <li><strong>Reviews page</strong> - Sync reviews & manage frequency</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2">
                      This component displays reviews from the database when added to any page.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingComponent.config?.title || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, title: e.target.value}})}
                      placeholder="e.g., What Our Clients Say"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle (optional)</label>
                    <input
                      type="text"
                      value={editingComponent.config?.subtitle || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, subtitle: e.target.value}})}
                      placeholder="e.g., Don't just take our word for it"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Reviews to Show</label>
                    <input
                      type="number"
                      value={editingComponent.config?.max_reviews || 6}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, max_reviews: parseInt(e.target.value) || 6}})}
                      min={1}
                      max={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingComponent.config?.show_carousel !== false}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_carousel: e.target.checked}})}
                        className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                      />
                      <span className="text-sm text-gray-700">Show as Carousel</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Contact Info Config */}
              {editingComponent.type === 'contact_info' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Contact Info Settings</h3>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingComponent.config?.show_phone !== false}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_phone: e.target.checked}})}
                        className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                      />
                      <span className="text-sm text-gray-700">Show Phone</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingComponent.config?.show_address !== false}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_address: e.target.checked}})}
                        className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                      />
                      <span className="text-sm text-gray-700">Show Address</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingComponent.config?.show_hours !== false}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_hours: e.target.checked}})}
                        className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                      />
                      <span className="text-sm text-gray-700">Show Hours</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Product / Service Listing Config */}
              {editingComponent.type === 'product_listing' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Listing Settings</h3>
                  
                  {/* Listing Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Listing Type *</label>
                    <select
                      value={editingComponent.config?.listing_type || ''}
                      onChange={(e) => {
                        setEditingComponent({
                          ...editingComponent, 
                          config: {
                            ...editingComponent.config, 
                            listing_type: e.target.value,
                            filter_mode: 'category', // Reset filter mode
                            selected_categories: [], // Reset categories when type changes
                            selected_menu_category: '' // Reset menu category
                          }
                        });
                        setProductSubCategories([]); // Reset sub-categories
                        fetchProductListingCategories();
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    >
                      <option value="">-- Select Listing Type --</option>
                      <option value="products">Products (E-Commerce Menu Items)</option>
                      <option value="services">Services (Appointment Services)</option>
                      <option value="boarding">Boarding Spaces</option>
                    </select>
                  </div>

                  {/* Filter Mode Selection - Only show when listing type is selected */}
                  {editingComponent.config?.listing_type && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Filter Mode *</label>
                      <div className="grid grid-cols-2 gap-3">
                        <label 
                          className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            editingComponent.config?.filter_mode === 'component_enabled' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="filter_mode"
                            value="component_enabled"
                            checked={editingComponent.config?.filter_mode === 'component_enabled'}
                            onChange={() => setEditingComponent({
                              ...editingComponent, 
                              config: {
                                ...editingComponent.config, 
                                filter_mode: 'component_enabled',
                                selected_menu_category: '',
                                selected_categories: []
                              }
                            })}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Component Enabled</span>
                            <p className="text-xs text-gray-500">Show items marked for component display</p>
                          </div>
                        </label>
                        <label 
                          className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            editingComponent.config?.filter_mode !== 'component_enabled' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="filter_mode"
                            value="category"
                            checked={editingComponent.config?.filter_mode !== 'component_enabled'}
                            onChange={() => setEditingComponent({
                              ...editingComponent, 
                              config: {
                                ...editingComponent.config, 
                                filter_mode: 'category'
                              }
                            })}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">By Category</span>
                            <p className="text-xs text-gray-500">Filter by menu and product categories</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Section Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingComponent.config?.section_title || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, section_title: e.target.value}})}
                      placeholder="e.g., Our Products, Popular Services"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>

                  {/* Section Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Subtitle</label>
                    <input
                      type="text"
                      value={editingComponent.config?.section_subtitle || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, section_subtitle: e.target.value}})}
                      placeholder="e.g., Browse our selection of premium products"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                    />
                  </div>

                  {/* Number of Items */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number of Items to Display</label>
                      <select
                        value={editingComponent.config?.items_count || 6}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, items_count: parseInt(e.target.value)}})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      >
                        <option value={3}>3 Items</option>
                        <option value={4}>4 Items</option>
                        <option value={6}>6 Items</option>
                        <option value={8}>8 Items</option>
                        <option value={12}>12 Items</option>
                        <option value={16}>16 Items</option>
                        <option value={0}>All Items</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Grid Columns</label>
                      <select
                        value={editingComponent.config?.grid_columns || 3}
                        onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, grid_columns: parseInt(e.target.value)}})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                      >
                        <option value={2}>2 Columns</option>
                        <option value={3}>3 Columns</option>
                        <option value={4}>4 Columns</option>
                      </select>
                    </div>
                  </div>

                  {/* Menu Category Selection - Only show when filter mode is 'category' */}
                  {editingComponent.config?.listing_type && editingComponent.config?.filter_mode !== 'component_enabled' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Filter by Menu Category
                      </label>
                      {loadingCategories ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                          Loading menu categories...
                        </div>
                      ) : (
                        <select
                          value={editingComponent.config?.selected_menu_category || ''}
                          onChange={(e) => {
                            const menuCatId = e.target.value;
                            setEditingComponent({
                              ...editingComponent, 
                              config: {
                                ...editingComponent.config, 
                                selected_menu_category: menuCatId,
                                selected_categories: [] // Reset product categories when menu changes
                              }
                            });
                            if (editingComponent.config?.listing_type === 'products') {
                              fetchProductSubCategories(menuCatId);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                        >
                          <option value="">-- All Menu Categories --</option>
                          {editingComponent.config?.listing_type === 'products' && productListingCategories.products.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                          {editingComponent.config?.listing_type === 'services' && productListingCategories.services.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.menu_category_name || cat.name}</option>
                          ))}
                          {editingComponent.config?.listing_type === 'boarding' && productListingCategories.boarding.map((space) => (
                            <option key={space.id} value={space.id}>{space.name || space.space_name}</option>
                          ))}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={fetchProductListingCategories}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Menu Categories
                      </button>
                    </div>
                  )}

                  {/* Product Categories Selection - Only show for products when filter mode is 'category' and a menu category is selected */}
                  {editingComponent.config?.listing_type === 'products' && editingComponent.config?.filter_mode !== 'component_enabled' && editingComponent.config?.selected_menu_category && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Filter by Product Categories
                        <span className="text-gray-400 font-normal ml-1">(Leave empty for all)</span>
                      </label>
                      {loadingSubCategories ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                          Loading product categories...
                        </div>
                      ) : productSubCategories.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                          {productSubCategories.map((category) => (
                            <label key={category} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={(editingComponent.config?.selected_categories || []).includes(category)}
                                onChange={(e) => {
                                  const current = editingComponent.config?.selected_categories || [];
                                  const updated = e.target.checked 
                                    ? [...current, category]
                                    : current.filter(c => c !== category);
                                  setEditingComponent({...editingComponent, config: {...editingComponent.config, selected_categories: updated}});
                                }}
                                className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                              />
                              <span className="text-sm text-gray-700">{category}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic py-2">No product categories found in this menu.</p>
                      )}
                    </div>
                  )}

                  {/* View All Button Settings */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">View All Button</h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_view_all_button !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_view_all_button: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show View All button</span>
                      </label>
                      
                      {editingComponent.config?.show_view_all_button !== false && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                            <input
                              type="text"
                              value={editingComponent.config?.view_all_text || 'View All'}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, view_all_text: e.target.value}})}
                              placeholder="View All"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                            <input
                              type="text"
                              value={editingComponent.config?.view_all_link || ''}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, view_all_link: e.target.value}})}
                              placeholder={
                                editingComponent.config?.listing_type === 'products' ? '/order-online' :
                                editingComponent.config?.listing_type === 'services' ? '/book-appointment' :
                                '/book-boarding'
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Display Options */}
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Display Options</h4>
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_price !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_price: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Price</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.show_description !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_description: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Show Description</span>
                      </label>
                      {editingComponent.config?.listing_type === 'products' && (
                        <>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingComponent.config?.show_add_to_cart !== false}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_add_to_cart: e.target.checked}})}
                              className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                            />
                            <span className="text-sm text-gray-700">Show Add to Cart Button</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={editingComponent.config?.show_stock_status !== false}
                              onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_stock_status: e.target.checked}})}
                              className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                            />
                            <span className="text-sm text-gray-700">Show Stock Status</span>
                          </label>
                        </>
                      )}
                      {editingComponent.config?.listing_type === 'services' && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingComponent.config?.show_duration !== false}
                            onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, show_duration: e.target.checked}})}
                            className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                          />
                          <span className="text-sm text-gray-700">Show Duration</span>
                        </label>
                      )}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingComponent.config?.enable_modal !== false}
                          onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, enable_modal: e.target.checked}})}
                          className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                        />
                        <span className="text-sm text-gray-700">Open Details in Modal on Click</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Content Config */}
              {editingComponent.type === 'custom' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase">Custom Content</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HTML/Content</label>
                    <textarea
                      value={editingComponent.config?.content || ''}
                      onChange={(e) => setEditingComponent({...editingComponent, config: {...editingComponent.config, content: e.target.value}})}
                      placeholder="Enter custom HTML or content..."
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-blue focus:border-transparent font-mono text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Publish Toggle */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingComponent.is_published}
                    onChange={(e) => setEditingComponent({...editingComponent, is_published: e.target.checked})}
                    className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-theme-blue"
                  />
                  <span className="text-sm text-gray-700">Published (available for use in pages)</span>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeComponentModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveComponent}
                disabled={saving || !editingComponent.name || !editingComponent.slug}
                className="px-4 py-2 bg-theme-blue text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Component
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Component Config Edit Modal */}
      {showPageComponentModal && editingPageComponent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Edit Component Settings</h3>
                <p className="text-sm text-white/80">
                  {editingPageComponent.baseComponent?.name} - Customize for this page
                </p>
              </div>
              <button 
                onClick={() => {
                  setShowPageComponentModal(false);
                  setEditingPageComponent(null);
                  setEditingPageComponentIndex(null);
                }}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Reviews Component - Not editable at page level */}
              {editingPageComponent.baseComponent?.type === 'reviews' && (
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">Reviews Component</h4>
                    <p className="text-sm text-blue-700">
                      The Reviews component is configured globally and displays the same reviews across all pages.
                    </p>
                    <p className="text-sm text-blue-700 mt-2">
                      To configure the Google Business URL, fetch settings, or manage reviews, please edit this component in the <strong>Components tab</strong>.
                    </p>
                    <button
                      onClick={() => {
                        setShowPageComponentModal(false);
                        setActiveTab('components');
                        // Find and edit the reviews component
                        const reviewsComp = components.find(c => c.id === editingPageComponent.component_id);
                        if (reviewsComp) {
                          setEditingComponent(reviewsComp);
                          setShowComponentModal(true);
                        }
                      }}
                      className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Go to Component Settings
                    </button>
                  </div>
                </div>
              )}

              {/* CTA Section Config */}
              {editingPageComponent.baseComponent?.type === 'cta_section' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">CTA Section Settings</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, title: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editingPageComponent.config?.description || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, description: e.target.value }
                      })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.button_text || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, button_text: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.button_link || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, button_link: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Dual Button CTA Config */}
              {editingPageComponent.baseComponent?.type === 'dual_button_cta' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Dual Button CTA Settings</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, title: e.target.value }
                      })}
                      placeholder="e.g., Experience Our Mission in Action"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editingPageComponent.config?.description || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, description: e.target.value }
                      })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Text</label>
                      <input
                        type="text"
                        value={editingPageComponent.config?.primary_button_text || ''}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, primary_button_text: e.target.value }
                        })}
                        placeholder="e.g., Call Now"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Link</label>
                      <input
                        type="text"
                        value={editingPageComponent.config?.primary_button_link || ''}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, primary_button_link: e.target.value }
                        })}
                        placeholder="Leave empty for tel: link"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Text</label>
                      <input
                        type="text"
                        value={editingPageComponent.config?.secondary_button_text || ''}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, secondary_button_text: e.target.value }
                        })}
                        placeholder="e.g., Schedule Visit"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Link</label>
                      <input
                        type="text"
                        value={editingPageComponent.config?.secondary_button_link || ''}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, secondary_button_link: e.target.value }
                        })}
                        placeholder="e.g., /reach-us"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Card Section Config */}
              {editingPageComponent.baseComponent?.type === 'card_section' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Card Section Settings</h4>
                  <p className="text-sm text-gray-600">Customize this card section for this specific page</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, title: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Subtitle</label>
                    <textarea
                      value={editingPageComponent.config?.subtitle || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, subtitle: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Style</label>
                    <select
                      value={editingPageComponent.config?.background_style || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, background_style: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Use Default</option>
                      <option value="white">White Background</option>
                      <option value="theme">Theme Color Background</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.button_text || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, button_text: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.button_link || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, button_link: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPageComponent.config?.show_button !== false}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, show_button: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Show Button</span>
                    </label>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-sm font-semibold text-gray-700">Cards</h5>
                      <button
                        type="button"
                        onClick={() => {
                          const cards = editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [];
                          setEditingPageComponent({
                            ...editingPageComponent,
                            config: {
                              ...editingPageComponent.config,
                              cards: [
                                ...cards,
                                {
                                  title: 'Card Title',
                                  description: 'Card description',
                                  icon: 'check-circle',
                                  icon_color: 'var(--theme-primary)',
                                  icon_bg: 'var(--theme-primary-light)',
                                  link: '',
                                  link_text: ''
                                }
                              ]
                            }
                          });
                        }}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        + Add Card
                      </button>
                    </div>

                    {((editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || []).length === 0) && (
                      <p className="text-sm text-gray-500 text-center py-4 border border-gray-200 rounded-lg">
                        No cards added yet. Click &quot;Add Card&quot; to create cards.
                      </p>
                    )}

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || []).map((card, cardIndex) => (
                        <div key={cardIndex} className="p-3 border border-gray-200 rounded-lg space-y-2 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <h6 className="text-xs font-medium text-gray-700">Card {cardIndex + 1}</h6>
                            <button
                              type="button"
                              onClick={() => {
                                const cards = [...(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [])];
                                cards.splice(cardIndex, 1);
                                setEditingPageComponent({
                                  ...editingPageComponent,
                                  config: {
                                    ...editingPageComponent.config,
                                    cards
                                  }
                                });
                              }}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Icon</label>
                              <select
                                value={card.icon || 'check-circle'}
                                onChange={(e) => {
                                  const cards = [...(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [])];
                                  cards[cardIndex] = { ...cards[cardIndex], icon: e.target.value };
                                  setEditingPageComponent({
                                    ...editingPageComponent,
                                    config: {
                                      ...editingPageComponent.config,
                                      cards
                                    }
                                  });
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="check-circle">Check Circle</option>
                                <option value="heart">Heart</option>
                                <option value="shield">Shield</option>
                                <option value="star">Star</option>
                                <option value="award">Award</option>
                                <option value="users">Users</option>
                                <option value="clock">Clock</option>
                                <option value="map-pin">Map Pin</option>
                                <option value="phone">Phone</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                              <input
                                type="text"
                                value={card.title || ''}
                                onChange={(e) => {
                                  const cards = [...(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [])];
                                  cards[cardIndex] = { ...cards[cardIndex], title: e.target.value };
                                  setEditingPageComponent({
                                    ...editingPageComponent,
                                    config: {
                                      ...editingPageComponent.config,
                                      cards
                                    }
                                  });
                                }}
                                placeholder="Card title"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={card.description || ''}
                              onChange={(e) => {
                                const cards = [...(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [])];
                                cards[cardIndex] = { ...cards[cardIndex], description: e.target.value };
                                setEditingPageComponent({
                                  ...editingPageComponent,
                                  config: {
                                    ...editingPageComponent.config,
                                    cards
                                  }
                                });
                              }}
                              placeholder="Card description"
                              rows={2}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Link (optional)</label>
                              <input
                                type="text"
                                value={card.link || ''}
                                onChange={(e) => {
                                  const cards = [...(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [])];
                                  cards[cardIndex] = { ...cards[cardIndex], link: e.target.value };
                                  setEditingPageComponent({
                                    ...editingPageComponent,
                                    config: {
                                      ...editingPageComponent.config,
                                      cards
                                    }
                                  });
                                }}
                                placeholder="/page-url"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Link Text</label>
                              <input
                                type="text"
                                value={card.link_text || ''}
                                onChange={(e) => {
                                  const cards = [...(editingPageComponent.config?.cards || editingPageComponent.baseComponent?.config?.cards || [])];
                                  cards[cardIndex] = { ...cards[cardIndex], link_text: e.target.value };
                                  setEditingPageComponent({
                                    ...editingPageComponent,
                                    config: {
                                      ...editingPageComponent.config,
                                      cards
                                    }
                                  });
                                }}
                                placeholder="Learn More"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Home Hero Section Config */}
              {editingPageComponent.baseComponent?.type === 'home_hero' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Home Hero Section Settings</h4>
                  <p className="text-sm text-gray-600">Customize this hero section for this specific page</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Layout Style</label>
                    <select
                      value={editingPageComponent.config?.layout || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, layout: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Use Default</option>
                      <option value="split">Split Layout</option>
                      <option value="full_width">Full Width Hero</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.main_title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, main_title: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Highlighted Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.main_title_highlight || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, main_title_highlight: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editingPageComponent.config?.description || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, description: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPageComponent.config?.show_slider_text !== false}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, show_slider_text: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Show Slider Image Text Overlay</span>
                    </label>
                  </div>

                  <div className="space-y-2 border-t border-gray-200 pt-4">
                    <h5 className="text-xs font-semibold text-gray-600 uppercase">Visibility Options</h5>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPageComponent.config?.show_call_button !== false}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, show_call_button: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Show Call Button</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPageComponent.config?.show_checkin_button !== false}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, show_checkin_button: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Show Check In Button</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingPageComponent.config?.show_slider_dots !== false}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, show_slider_dots: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Show Slider Dots</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Service Areas Config */}
              {editingPageComponent.baseComponent?.type === 'service_areas' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Service Areas Settings</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, title: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Product/Service Listing Config */}
              {editingPageComponent.baseComponent?.type === 'product_listing' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Product/Service Listing Settings</h4>
                  <p className="text-sm text-gray-600">Configure what items to display in this listing</p>
                  
                  {/* Listing Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Listing Type *</label>
                    <select
                      value={editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type || ''}
                      onChange={(e) => {
                        setEditingPageComponent({
                          ...editingPageComponent,
                          config: { 
                            ...editingPageComponent.config, 
                            listing_type: e.target.value,
                            filter_mode: 'category',
                            selected_menu_category: '',
                            selected_categories: []
                          }
                        });
                        setProductSubCategories([]);
                        fetchProductListingCategories();
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select Listing Type --</option>
                      <option value="products">Products (E-Commerce Menu Items)</option>
                      <option value="services">Services (Appointment Services)</option>
                      <option value="boarding">Boarding Spaces</option>
                    </select>
                  </div>

                  {/* Filter Mode */}
                  {(editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Filter Mode</label>
                      <div className="grid grid-cols-2 gap-3">
                        <label 
                          className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            editingPageComponent.config?.filter_mode === 'component_enabled' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="page_filter_mode"
                            value="component_enabled"
                            checked={editingPageComponent.config?.filter_mode === 'component_enabled'}
                            onChange={() => setEditingPageComponent({
                              ...editingPageComponent, 
                              config: {
                                ...editingPageComponent.config, 
                                filter_mode: 'component_enabled',
                                selected_menu_category: '',
                                selected_categories: []
                              }
                            })}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">Component Enabled</span>
                            <p className="text-xs text-gray-500">Show CMS-enabled items</p>
                          </div>
                        </label>
                        <label 
                          className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            editingPageComponent.config?.filter_mode !== 'component_enabled' 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="page_filter_mode"
                            value="category"
                            checked={editingPageComponent.config?.filter_mode !== 'component_enabled'}
                            onChange={() => setEditingPageComponent({
                              ...editingPageComponent, 
                              config: {
                                ...editingPageComponent.config, 
                                filter_mode: 'category'
                              }
                            })}
                            className="w-4 h-4 text-blue-600"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">By Category</span>
                            <p className="text-xs text-gray-500">Filter by categories</p>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Menu Category Selection - Only show when filter mode is 'category' */}
                  {(editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type) && 
                   editingPageComponent.config?.filter_mode !== 'component_enabled' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Menu Category</label>
                      {loadingCategories ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                          Loading...
                        </div>
                      ) : (
                        <select
                          value={editingPageComponent.config?.selected_menu_category || ''}
                          onChange={(e) => {
                            const menuCatId = e.target.value;
                            setEditingPageComponent({
                              ...editingPageComponent, 
                              config: {
                                ...editingPageComponent.config, 
                                selected_menu_category: menuCatId,
                                selected_categories: []
                              }
                            });
                            const listingType = editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type;
                            if (listingType === 'products') {
                              fetchProductSubCategories(menuCatId);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">-- All Menu Categories --</option>
                          {(editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type) === 'products' && 
                            productListingCategories.products.map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))
                          }
                          {(editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type) === 'services' && 
                            productListingCategories.services.map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.menu_category_name || cat.name}</option>
                            ))
                          }
                          {(editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type) === 'boarding' && 
                            productListingCategories.boarding.map((space) => (
                              <option key={space.id} value={space.id}>{space.name || space.space_name}</option>
                            ))
                          }
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={fetchProductListingCategories}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Categories
                      </button>
                    </div>
                  )}

                  {/* Product Categories Selection */}
                  {(editingPageComponent.config?.listing_type || editingPageComponent.baseComponent?.config?.listing_type) === 'products' && 
                   editingPageComponent.config?.filter_mode !== 'component_enabled' && 
                   editingPageComponent.config?.selected_menu_category && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Categories
                        <span className="text-gray-400 font-normal ml-1">(Leave empty for all)</span>
                      </label>
                      {loadingSubCategories ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                          Loading...
                        </div>
                      ) : productSubCategories.length > 0 ? (
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                          {productSubCategories.map((category) => (
                            <label key={category} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={(editingPageComponent.config?.selected_categories || []).includes(category)}
                                onChange={(e) => {
                                  const current = editingPageComponent.config?.selected_categories || [];
                                  const updated = e.target.checked 
                                    ? [...current, category]
                                    : current.filter(c => c !== category);
                                  setEditingPageComponent({
                                    ...editingPageComponent,
                                    config: { ...editingPageComponent.config, selected_categories: updated }
                                  });
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-700">{category}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic py-2">No product categories in this menu.</p>
                      )}
                    </div>
                  )}

                  {/* Section Title Override */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.section_title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, section_title: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Section Subtitle Override */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Subtitle</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.section_subtitle || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, section_subtitle: e.target.value }
                      })}
                      placeholder="Leave empty to use default"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Items Count and Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Items to Display</label>
                      <select
                        value={editingPageComponent.config?.items_count ?? editingPageComponent.baseComponent?.config?.items_count ?? 6}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, items_count: parseInt(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={3}>3 Items</option>
                        <option value={4}>4 Items</option>
                        <option value={6}>6 Items</option>
                        <option value={8}>8 Items</option>
                        <option value={12}>12 Items</option>
                        <option value={0}>All Items</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Grid Columns</label>
                      <select
                        value={editingPageComponent.config?.grid_columns ?? editingPageComponent.baseComponent?.config?.grid_columns ?? 3}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, grid_columns: parseInt(e.target.value) }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={2}>2 Columns</option>
                        <option value={3}>3 Columns</option>
                        <option value={4}>4 Columns</option>
                      </select>
                    </div>
                  </div>

                  {/* View All Button */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="flex items-center gap-2 cursor-pointer mb-3">
                      <input
                        type="checkbox"
                        checked={editingPageComponent.config?.show_view_all_button !== false}
                        onChange={(e) => setEditingPageComponent({
                          ...editingPageComponent,
                          config: { ...editingPageComponent.config, show_view_all_button: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">Show View All Button</span>
                    </label>
                    
                    {editingPageComponent.config?.show_view_all_button !== false && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                          <input
                            type="text"
                            value={editingPageComponent.config?.view_all_text || ''}
                            onChange={(e) => setEditingPageComponent({
                              ...editingPageComponent,
                              config: { ...editingPageComponent.config, view_all_text: e.target.value }
                            })}
                            placeholder="View All"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                          <input
                            type="text"
                            value={editingPageComponent.config?.view_all_link || ''}
                            onChange={(e) => setEditingPageComponent({
                              ...editingPageComponent,
                              config: { ...editingPageComponent.config, view_all_link: e.target.value }
                            })}
                            placeholder="/order-online"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Info Config */}
              {editingPageComponent.baseComponent?.type === 'contact_info' && (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Contact Info Settings</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={editingPageComponent.config?.title || ''}
                      onChange={(e) => setEditingPageComponent({
                        ...editingPageComponent,
                        config: { ...editingPageComponent.config, title: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPageComponentModal(false);
                  setEditingPageComponent(null);
                  setEditingPageComponentIndex(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save the config to the page component
                  const newComponents = [...editingPage.components];
                  newComponents[editingPageComponentIndex] = {
                    component_id: editingPageComponent.component_id,
                    config: editingPageComponent.config
                  };
                  setEditingPage({ ...editingPage, components: newComponents });
                  setShowPageComponentModal(false);
                  setEditingPageComponent(null);
                  setEditingPageComponentIndex(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                Apply Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Page Modal Component - Compact with 3 fields only
const CreatePageModal = ({ onClose, onCreate, parentPath, layout = 'default' }) => {
  const [formData, setFormData] = useState({
    title: '',
    path: parentPath || '',
    layout: layout,
    is_published: false
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.path) return;
    
    setCreating(true);
    // Ensure path starts with /
    const path = formData.path.startsWith('/') ? formData.path : '/' + formData.path;
    await onCreate({ ...formData, path });
    setCreating(false);
  };

  const generateSlug = (title) => {
    const slug = title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    // If parentPath exists, append to it (remove trailing slash if present)
    if (parentPath && parentPath !== '/') {
      const cleanParentPath = parentPath.endsWith('/') ? parentPath.slice(0, -1) : parentPath;
      return cleanParentPath + '/' + slug;
    }
    return '/' + slug;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Compact Header - Website Themed */}
        <div className="px-4 py-3 bg-blue-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileEdit className="h-4 w-4 text-white" />
            <h3 className="text-sm font-semibold text-white">New Inner Page</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        {/* Modal Body - Simple 3 Fields */}
        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-3">
            {/* Page Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    title: e.target.value,
                    path: generateSlug(e.target.value)
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="My New Page"
                autoFocus
                required
              />
            </div>

            {/* Page Path */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Page Path <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.path}
                onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="/services/my-new-page"
                required
              />
              <p className="text-xs text-gray-400 mt-1">URL path for this page</p>
            </div>

            {/* Layout */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Layout</label>
              <select
                value={formData.layout}
                onChange={(e) => setFormData({ ...formData, layout: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                <option value="default">Default (Centered Hero)</option>
                <option value="image_right">Image Right</option>
                <option value="full_width">Full Width</option>
              </select>
            </div>
          </div>

          {/* Compact Footer */}
          <div className="px-4 py-3 bg-gray-50 flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !formData.title || !formData.path}
              className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {creating ? (
                <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Plus className="h-3 w-3" />
                  Create
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentManager;
